import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import _ from "lodash";
import { toDisplayTime } from "../utilis/date-formats";
import { getResidenceList as fetchResidenceList } from "../services/api/residence-list.api";
import { getAccountQuestions } from "../services/api/user.api";
import { QUESTION_INPUT_TYPE } from "../types/question.type";
import { PAGE_TYPE } from "../types/pages.type";
import { CUSTOM_TAB_TYPE, TOTAL_TYPE } from "../types/common.type";
import { HOSPITAL_CARDS_LABELS } from "../types/hospital.type";
import { getCustomTabsCards } from "../utilis/custom-tab-filter";
import { COMMUNITY_CARD_LABELS } from "../types/community-transfer.type";
import {
  Box,
  Button,
  ButtonBase,
  Checkbox,
  Collapse,
  Drawer,
  Divider,
  FormControl,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  IconButton,
  Typography,
  Popover,
  Tooltip,
  useMediaQuery
} from "@mui/material";
import { getCensusFirstDate } from "../services/data-input.service";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import ShareIcon from "@mui/icons-material/Share";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ExcelJS from "exceljs";
import { autoFitColumns, styleHeaderRow } from "../utilis/excelFormatter";
import useShareReport from "../hooks/useShareReport";
import useAccountId from "../hooks/useAccountId";
import ShareNotesDialog from "../components/shared/notes/ShareNotesDialog";
import styles from "./ResidenceList.module.scss";
import AddPatient from "../components/landing/facility/data/add-patient/AddPatient";
import RotateScreenDialog from "../components/shared/RotateScreenDialog";
import EditSVG from "../assets/svgs/edit.svg";
import InfoIcon from "../components/icon/InfoIcon";
import ActivityLogTooltip from "../components/shared/ActivityLogTooltip";
import HtmlTooltip from "../components/shared/HtmlTooltip";
import {
  DndContext,
  closestCenter,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const PRIMARY = "#2259c9";
const HEADER_CONTROL_HEIGHT = 40;
const MOBILE_HEADER_CONTROL_HEIGHT = 34;
const HEADER_GAP = 2;

const DAY_SHORT_TO_FULL = { Sun: "Sunday", Mon: "Monday", Tue: "Tuesday", Wed: "Wednesday", Thu: "Thursday", Fri: "Friday", Sat: "Saturday" };
const BASE_COLUMN_IDS = ["facility", "lastName", "firstName", "middleInitial", "dob", "dateOfADT", "type"];
const TRUNCATION_LIMIT = 22;   // Characters before text is truncated in table cells (ensures highlighted ellipsis fits)

/** Wrapper that makes a TableCell header draggable via @dnd-kit */
const DraggableTh = ({ id, sx, children, disabled, ...rest }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1, // Slightly more transparent to emphasize overlay
    cursor: disabled ? "default" : (isDragging ? "grabbing" : "grab"),
    zIndex: isDragging ? 100 : (sx?.zIndex || 10), // Use sx.zIndex if provided
    left: sx?.left, // Ensure left is applied to inline style for highest priority
  };

  return (
    <TableCell
      ref={setNodeRef}
      style={style}
      sx={{
        ...sx,
        position: "sticky",
        top: 0,
        overflow: "visible", // To allow interactive indicator to be hoverable
      }}
      {...attributes}
      {...listeners}
      {...rest}
    >
      {children}
    </TableCell>
  );
};

const ResidenceList = () => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isPortrait = useMediaQuery("(orientation: portrait)");
  const [showRotateAlert, setShowRotateAlert] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isMobileFiltersCollapsed, setIsMobileFiltersCollapsed] = useState(true);

  useEffect(() => {
    if (isMobile && isPortrait) {
      if (!isDismissed) {
        setShowRotateAlert(true);
      }
    } else {
      setShowRotateAlert(false);
      setIsDismissed(false);
    }
  }, [isMobile, isPortrait, isDismissed]);

  const { facilities: activeFacilities } = useSelector((state) => state.activeFacilities);
  const { permission } = useSelector(({ permission }) => ({ permission }));
  const { selectedAccount } = useSelector((state) => state.common);
  const { accountId } = useAccountId();

  const [filters, setFilters] = useState({
    search: "",
    type: "all", // all, hospitalTransfer, communityTransfer, deceased, admission
    admissionType: [], // [] = all, ["admission","reAdmission"] = multi-select
    transferType: [], // [] = all; hospital: ["plannedHospitalTransfer","unplannedHospitalTransfer"]; community: ["safeDischarge","SNF","AMA"]
    fromDate: moment().subtract(30, "day").format("YYYY-MM-DD"),
    toDate: moment().format("YYYY-MM-DD"),
  });
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("dateOfADT");
  const [sortDirection, setSortDirection] = useState("desc");
  const [selectedResident, setSelectedResident] = useState(null);
  const [previousColor, setPreviousColor] = useState(null);
  const [initialDate, setInitialDate] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editPatient, setEditPatient] = useState(null);
  const [patientId, setPatientId] = useState(null);
  const [detailsLoading] = useState(false);
  const [detailsList] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [customTabsByPage, setCustomTabsByPage] = useState({});
  const [diffDashboardPatients, setDiffDashboardPatients] = useState({});
  const [customTabCardData, setCustomTabCardData] = useState({});
  const [customTabFilterValues, setCustomTabFilterValues] = useState({}); // { [accessor]: [selectedItemIds] }
  const [questionFilterValues, setQuestionFilterValues] = useState({});
  const [selectedQuestionColumnIds, setSelectedQuestionColumnIds] = useState([]);
  const [questionColumnOrder, setQuestionColumnOrder] = useState([]);
  const [hospitalizationFilter, setHospitalizationFilter] = useState([]); // [] = all, ["new","re"] = multi-select
  const [dcerFilter, setDcerFilter] = useState([]); // [] = all, ["DC","ER"] = multi-select
  const [returnsFilter, setReturnsFilter] = useState([]); // [] = all, ["returned","didntReturn"] = multi-select
  const [daysFilter, setDaysFilter] = useState([]); // ['Monday', ...]
  const [shiftFilter, setShiftFilter] = useState([]); // ['Morning', ...]
  const [ninetyDaysFilter, setNinetyDaysFilter] = useState([]); // ['a', 'b', ...]
  const [ninetyDaysOptions, setNinetyDaysOptions] = useState([]); // backend-driven 90-day buckets
  const [advancedFiltersAnchorEl, setAdvancedFiltersAnchorEl] = useState(null);
  const [advancedFiltersQuery, setAdvancedFiltersQuery] = useState("");
  const [expandedFilterSections, setExpandedFilterSections] = useState({
    transferType: false,
    hospitalization: false,
    dcer: false,
    returns: false,
    ninetyDays: false,
    dayOfWeek: false,
    shift: false,
    communityTransferType: false,
    admissionType: false,
  });
  const [columnFilterAnchor, setColumnFilterAnchor] = useState({ id: null, anchorEl: null });
  const [hiddenColumnsAnchorEl, setHiddenColumnsAnchorEl] = useState(null);
  const [advancedFiltersSummaryAnchorEl, setAdvancedFiltersSummaryAnchorEl] = useState(null);
  const [searchColumnsAnchorEl, setSearchColumnsAnchorEl] = useState(null);
  const [searchColumnAnchorEl, setSearchColumnAnchorEl] = useState(null);
  const [searchColumnQuery, setSearchColumnQuery] = useState("");
  const [columnMenuQuery, setColumnMenuQuery] = useState("");
  const [highlightedColumnIndex, setHighlightedColumnIndex] = useState(null);
  const highlightTimeoutRef = useRef(null);
  const scrollHighlightTimeoutRef = useRef(null);
  const tableContainerRef = useRef(null);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const scrollDirectionRef = useRef(null); // 'horizontal', 'vertical', or null
  const [isScrollingVertically, setIsScrollingVertically] = useState(false);
  const [isScrollingHorizontally, setIsScrollingHorizontally] = useState(false);

  const handleTouchStart = (e) => {
    if (!isMobile) return;
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    scrollDirectionRef.current = null;
  };

  const handleTouchMove = (e) => {
    if (!isMobile) return;
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);

    if (!scrollDirectionRef.current && (deltaX > 5 || deltaY > 5)) {
      if (deltaY > deltaX) {
        scrollDirectionRef.current = 'vertical';
        setIsScrollingVertically(true);
      } else {
        scrollDirectionRef.current = 'horizontal';
        setIsScrollingHorizontally(true);
      }
    }
  };

  const handleTouchEnd = () => {
    scrollDirectionRef.current = null;
    setIsScrollingVertically(false);
    setIsScrollingHorizontally(false);
  };

  const lastTypeRef = useRef(filters.type);
  const [selectedSearchColumnIds, setSelectedSearchColumnIds] = useState([
    "facility",
    "lastName",
    "firstName",
    "middleInitial",
    "dob",
    "dateOfADT",
    "type",
  ]);

  // Base columns (facility, lastName, firstName, middleInitial, dob, dateOfADT): order, visibility, column-level filters
  const [baseColumnOrder, setBaseColumnOrder] = useState(["facility", "lastName", "firstName", "middleInitial", "dob", "dateOfADT", "type"]);
  const [selectedBaseColumnIds, setSelectedBaseColumnIds] = useState(["facility", "lastName", "firstName", "middleInitial", "dob", "dateOfADT", "type"]);
  const [baseColumnFilterValues, setBaseColumnFilterValues] = useState({
    facility: "",
    firstName: "",
    middleInitial: "",
    lastName: "",
    dobFrom: "",
    dobTo: "",
    dateOfADTFrom: "",
    dateOfADTTo: "",
    type: "",
    notes: "",
  });
  const [baseColumnFilterAnchor, setBaseColumnFilterAnchor] = useState({ id: null, anchorEl: null });
  // dnd-kit: track the active (being-dragged) column id for DragOverlay
  const [activeColumnId, setActiveColumnId] = useState(null);
  // dnd-kit: require 5px movement before drag starts to avoid accidental drags on click
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );
  const [cellValueAnchorEl, setCellValueAnchorEl] = useState(null);
  const [currentCellValue, setCurrentCellValue] = useState("");
  // Per-column current navigation index:  { [colId]: number }
  const [currentSearchMatchIndices, setCurrentSearchMatchIndices] = useState({});
  const [currentCellColId, setCurrentCellColId] = useState(null);
  const [currentCellRowIndex, setCurrentCellRowIndex] = useState(null);
  const [globalSearchMatchIndex, setGlobalSearchMatchIndex] = useState(-1);
  const [activeHeaderTooltip, setActiveHeaderTooltip] = useState(null); // { id, title }

  const handleHeaderMouseEnter = useCallback((e, id, title) => {
    const el = e.currentTarget.querySelector(".truncated-text");
    if (el) {
      const isOverflow = el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
      if (isOverflow) {
        setActiveHeaderTooltip({ id, title });
      } else {
        setActiveHeaderTooltip(null);
      }
    }
  }, []);

  const isWriteAccess = React.useMemo(
    () => permission?.facilityAccess?.write ?? false,
    [permission]
  );

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        if (!activeFacilities?.length || !accountId) {
          setQuestions([]);
          return;
        }
        const res = await getAccountQuestions({
          facilityid: activeFacilities,
          accountId: accountId,
        });
        setQuestions(Array.isArray(res) ? res : []);
      } catch {
        setQuestions([]);
      }
    };
    fetchQuestions();
  }, [activeFacilities, accountId]);

  const adtTypes = useMemo(() => {
    const dashboardAccess = selectedAccount?.dashboardAccess || [];
    const options = [];

    if (_.includes(dashboardAccess, PAGE_TYPE.HOSPITAL)) {
      options.push({ value: "hospitalTransfer", label: "Hospital Transfer" });
    }
    if (_.includes(dashboardAccess, PAGE_TYPE.COMMUNITY_TRANSFER)) {
      options.push({ value: "communityTransfer", label: "Community Transfer" });
    }
    if (_.includes(dashboardAccess, PAGE_TYPE.DECEASED)) {
      options.push({ value: "deceased", label: "Deceased" });
    }
    if (_.includes(dashboardAccess, PAGE_TYPE.ADMISSION)) {
      options.push({ value: "admission", label: "Admission" });
    }

    if (options.length > 1) {
      options.unshift({ value: "all", label: "All" });
    }

    return options;
  }, [selectedAccount?.dashboardAccess]);

  useEffect(() => {
    if (adtTypes.length > 0) {
      const isValid = adtTypes.some((opt) => opt.value === filters.type);
      if (!isValid) {
        setFilters((f) => ({ ...f, type: adtTypes[0].value }));
      }
    }
  }, [adtTypes, filters.type]);

  const effectiveAdtForQuestions = useMemo(() => {
    if (filters.type === "admission") return "admission";
    if (["hospitalTransfer", "communityTransfer", "deceased"].includes(filters.type)) return "transfer";
    return null;
  }, [filters.type]);

  const currentDashboardPage = useMemo(() => {
    if (filters.type === "hospitalTransfer") return "hospital";
    if (filters.type === "communityTransfer") return "communityTransfer";
    if (filters.type === "deceased") return "deceased";
    if (filters.type === "admission") return "admission";
    return null;
  }, [filters.type]);

  const transferQuestionKey = useMemo(() => {
    if (effectiveAdtForQuestions !== "transfer") return null;
    if (filters.type === "hospitalTransfer") return "hospitalTransfer";
    if (filters.type === "communityTransfer") {
      const arr = Array.isArray(filters.transferType) ? filters.transferType : [];
      return arr.length === 0 ? "communityTransfer" : arr[0];
    }
    if (filters.type === "deceased") return "deceased";
    return null;
  }, [effectiveAdtForQuestions, filters.type, filters.transferType]);

  const getQuestionAnswer = useCallback((question, row) => {
    if (!question) return "—";
    const { accessor, type, validationType, multiple, customQuestionInputType, validationOptions, invisibleAnswerOptions, dataInputLabel } = question;
    if (!accessor) return "—";

    if (invisibleAnswerOptions && dataInputLabel) {
      const invisibleLabels = dataInputLabel.split(',').map(label => label.trim()).filter(Boolean);
      if (invisibleLabels.includes(row[accessor])) {
        return row[accessor];
      }
    }

    if (customQuestionInputType === QUESTION_INPUT_TYPE.UNLIMITED_ANSWERS && Array.isArray(validationOptions) && validationOptions.length > 0) {
      const foundOption = validationOptions.find((option) => option._id === row[accessor]);
      return foundOption?.label || "—";
    }

    switch (type) {
      case "text": {
        const value = row[accessor];
        if (value && typeof value === "object") {
          return value.value || value.label || "—";
        }
        return value || "—";
      }
      case "toggle": {
        const value = row[accessor];
        return value ? "Yes" : "No";
      }
      case "date": {
        if (accessor === "dateOfLatestAdmission") {
          const dateOfLatestAdmission = row[accessor] || row?.dateOfActualAdmission;
          return dateOfLatestAdmission ? toDisplayTime(dateOfLatestAdmission, "MM/DD/YYYY") : "—";
        } else {
          let val = row[accessor];
          if (val && typeof val === "object") {
            val = val.value || val.label;
          }
          return val ? toDisplayTime(val, "MM/DD/YYYY") : "—";
        }
      }
      case "time": {
        let value = row[accessor];
        if (value && typeof value === "object") {
          value = value.value || value.label;
        }
        return value || "—";
      }
      case "validation": {
        if (multiple) {
          const list = row[validationType] || [];
          const labels = Array.isArray(list) ? list.map((v) => v?.label).filter(Boolean) : [];
          const answer = labels.join(", ");
          return answer || "N/A";
        }
        const obj = row[validationType];
        return obj?.label || "N/A";
      }
      default: {
        const value = _.get(row, accessor);
        if (value && typeof value === "object") {
          return value.value || value.label || "—";
        }
        return value || "—";
      }
    }
  }, []);

  const extraColumns = useMemo(() => {
    if (!effectiveAdtForQuestions || !Array.isArray(questions) || questions.length === 0) {
      return [];
    }

    let filtered = questions.filter((q) => q.forType === effectiveAdtForQuestions);

    if (effectiveAdtForQuestions === "transfer" && transferQuestionKey) {
      filtered = filtered.filter((q) => q.forTransferType === transferQuestionKey);
    }

    const tabsForPage = currentDashboardPage && customTabsByPage?.[currentDashboardPage];
    const dynamicAccessorsFromTabs = new Set();
    if (Array.isArray(tabsForPage) && tabsForPage.length > 0) {
      tabsForPage.forEach((tab) => {
        if (!tab) return;
        if (tab.type === CUSTOM_TAB_TYPE.combineTab) return;
        const accessor = tab.existingAccessor || tab.accessor;
        if (accessor) {
          dynamicAccessorsFromTabs.add(accessor);
        }
      });
    }

    return filtered
      .map((ele) => {
        const q = ele?.question;
        if (
          !q?.accessor ||
          q.accessor === "transferType" ||
          q.accessor === "dateOfADT" ||
          q.customQuestionInputType === QUESTION_INPUT_TYPE.DATE_ANALYSIS
        )
          return null;
        const header =
          ele?.order?.tableLabel ||
          ele?.order?.label ||
          q?.tableLabel ||
          q?.label ||
          q?.accessor;

        const isFromCustomTab = !!q.customTab;
        const isMarkedCustomCard = !!q.useCustomCardInDashboard;
        const isCombineTab = q.customTab?.type === CUSTOM_TAB_TYPE.combineTab;
        const isLinkedToCustomTab = dynamicAccessorsFromTabs.has(q.accessor);
        const isDynamicCard = ((isFromCustomTab || isMarkedCustomCard) && !isCombineTab) || isLinkedToCustomTab;

        return {
          id: q.accessor,
          label: header,
          isDate: q.type === "date",
          isTime: q.type === "time",
          render: (row) => getQuestionAnswer(q, row),
          isDynamicCard,
        };
      })
      .filter(Boolean);
  }, [questions, effectiveAdtForQuestions, transferQuestionKey, getQuestionAnswer, customTabsByPage, currentDashboardPage]);

  const fetchData = useCallback(
    async (overrides = {}) => {
      const applied = {
        fromDate: overrides.fromDate ?? filters.fromDate,
        toDate: overrides.toDate ?? filters.toDate,
        type: overrides.type ?? filters.type,
      };
      const facilityIds = activeFacilities ?? [];
      if (!facilityIds.length) {
        setPatients([]);
        setDiffDashboardPatients({});
        return;
      }
      setLoading(true);
      try {
        let apiType = applied.type;
        let apiTransferType = undefined;
        let apiAdmissionType = undefined;

        if (applied.type === "hospitalTransfer") {
          apiType = "transfer";
          apiTransferType = "allHospitalTransfers";
        } else if (applied.type === "communityTransfer") {
          apiType = "transfer";
          apiTransferType = "allCommunityTransfers";
        } else if (applied.type === "deceased") {
          apiType = "transfer";
          apiTransferType = "deceased";
        } else if (applied.type === "admission") {
          apiType = "admissionReadmissions";
          apiAdmissionType = "allAdmissions";
        }

        const result = await fetchResidenceList({
          activeFacilities: facilityIds,
          type: apiType,
          fromdate: applied.fromDate,
          todate: applied.toDate,
          ...(apiAdmissionType && { admissionType: apiAdmissionType }),
          ...(apiTransferType && { transfertype: apiTransferType }),
        });
        const data = Array.isArray(result?.data) ? result.data : [];
        const ninetyDaysData = Array.isArray(result?.ninetyDaysData)
          ? result.ninetyDaysData
          : [];
        const apiCustomTabsByPage = result?.customTabsByPage && typeof result.customTabsByPage === "object"
          ? result.customTabsByPage
          : {};
        const apiDiffDashboardPatients = result?.diffDashboardPatients && typeof result.diffDashboardPatients === "object"
          ? result.diffDashboardPatients
          : {};
        setCustomTabsByPage(apiCustomTabsByPage);
        setDiffDashboardPatients(apiDiffDashboardPatients);
        setNinetyDaysOptions(ninetyDaysData);
        setPatients(
          data.map((row) => {
            const rowId = (row._id || row.id || "").toString();
            const bucketsForRow = ninetyDaysData
              .filter(
                (b) =>
                  Array.isArray(b.ids) &&
                  b.ids.map((id) => id.toString()).includes(rowId)
              )
              .map((b) => b._id);
            return {
              ...row,
              ninetyDaysBuckets: bucketsForRow,
            };
          })
        );
      } catch (err) {
        setPatients([]);
        setDiffDashboardPatients({});
      } finally {
        setLoading(false);
      }
    },
    [activeFacilities, filters.fromDate, filters.toDate, filters.type]
  );

  useEffect(() => {
    if (!activeFacilities?.length) {
      setPatients([]);
      setDiffDashboardPatients({});
      return;
    }
    fetchData();
  }, [activeFacilities, filters.fromDate, filters.toDate, filters.type, fetchData]);



  const handleResetAdvancedFilters = (extra = {}) => {
    setHospitalizationFilter([]);
    setDcerFilter([]);
    setReturnsFilter([]);
    setDaysFilter([]);
    setShiftFilter([]);
    setNinetyDaysFilter([]);
    setQuestionFilterValues({});
    const additionalFilters = (extra && typeof extra === 'object' && !extra.target) ? extra : {};
    setFilters((f) => ({ ...f, search: "", transferType: [], admissionType: [], ...additionalFilters }));
    setAdvancedFiltersAnchorEl(null);
    setAdvancedFiltersSummaryAnchorEl(null);
    setColumnFilterAnchor({ id: null, anchorEl: null });
    setBaseColumnFilterAnchor({ id: null, anchorEl: null });
    setBaseColumnFilterValues({
      facility: "",
      firstName: "",
      lastName: "",
      middleInitial: "",
      dobFrom: "",
      dobTo: "",
      dateOfADTFrom: "",
      dateOfADTTo: "",
      type: "",
    });
    setSortBy("dateOfADT");
    setSortDirection("desc");
  };

  useEffect(() => {
    const fetchInitialCensusDate = async () => {
      const facilityIdForCensus = activeFacilities?.[activeFacilities.length - 1];
      if (facilityIdForCensus) {
        try {
          const res = await getCensusFirstDate(facilityIdForCensus);
          if (res) {
            setInitialDate(res?.date);
          }
        } catch (error) {
          console.error("Error fetching census first date:", error);
        }
      }
    };
    fetchInitialCensusDate();
  }, [activeFacilities]);

  const handleScrollTo = (fieldName, color) => {
    if (previousColor && fieldName === previousColor?.fieldName) {
      const elementPrevious = document.getElementById(previousColor?.fieldName);
      if (elementPrevious) {
        elementPrevious.style.border = "";
        elementPrevious.style.borderBottom = "1.5px solid #E5E7EB";
      }
      setPreviousColor(null);
    } else {
      const element = document.getElementById(fieldName);

      if (scrollHighlightTimeoutRef.current) {
        clearTimeout(scrollHighlightTimeoutRef.current);
        scrollHighlightTimeoutRef.current = null;
      }

      if (previousColor) {
        const elementPrevious = document.getElementById(previousColor?.fieldName);
        if (elementPrevious) {
          elementPrevious.style.border = "";
          elementPrevious.style.borderBottom = "1.5px solid #E5E7EB";
          elementPrevious.style.transition = "";
        }
      }
      if (element) {
        element.style.transition = "border-color 2.5s linear";
        element.style.border = "3px solid " + color;

        // Set timeout to fade out
        scrollHighlightTimeoutRef.current = setTimeout(() => {
          element.style.borderColor = "transparent";
          // Also cleanup after fade finishes
          setTimeout(() => {
            element.style.border = "";
            element.style.borderBottom = "1.5px solid #E5E7EB";
            element.style.transition = "";
            if (previousColor?.fieldName === fieldName) setPreviousColor(null);
          }, 2500);
        }, 3000);

        // Custom scroll logic for ResidenceList container
        const parentContainer = element.closest(".MuiTableContainer-root");
        if (parentContainer) {
          // Calculate frozen column width to subtract from scroll position - Only if not "all"
          const isAllType = filters.type === 'all';
          const frozenWidth = isAllType ? 0 : ((filters.type !== 'all' ? (isMobile ? 40 : 80) : 0) + (showFacilityColumn ? (isMobile ? 100 : 140) : 0) + (isMobile ? 200 : 280));

          // Scroll horizontally to the element
          const containerLeft = parentContainer.getBoundingClientRect().left;
          const elementLeft = element.getBoundingClientRect().left;
          const scrollDelta = elementLeft - containerLeft;

          // Subtract frozenWidth to ensure element is visible past the sticky columns
          parentContainer.scrollTo({
            left: parentContainer.scrollLeft + (scrollDelta - frozenWidth - 10),
            ...(isMobile ? {} : { top: element.offsetTop - 10 }), // Only scroll vertically on desktop
            behavior: 'smooth'
          });
        }
        setPreviousColor({ fieldName, color });
      }
    }
  };

  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(columnKey);
      setSortDirection("asc");
    }
  };



  const formatTypeLabel = (row) => {
    if (row.type === "transfer" && row.transferType) {
      if (["SNF", "AMA"].includes(row.transferType)) return row.transferType;
      return row.transferType.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (str) => str.toUpperCase());
    }
    return row.type ? row.type.replace(/^./, (str) => str.toUpperCase()) : "—";
  };

  const showFacilityColumn = permission?.isMultipleFacility === true;


  // Map ResidenceList type -> backend customTabsByPage key (note: keys are lowercase)

  // Clear custom tab filter when switching ADT type
  useEffect(() => {
    setCustomTabFilterValues({});
  }, [filters.type]);

  // Fetch custom tab card data (same as Hospital dashboard) for advanced filter "Custom tabs" section
  useEffect(() => {
    const page = currentDashboardPage;
    const tabs = page && customTabsByPage?.[page];
    const nonCombineTabs = Array.isArray(tabs) ? tabs : [];

    if (nonCombineTabs.length === 0 || !patients.length) {
      setCustomTabCardData({});
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const totalFilterData = {
          originalData: patients,
          totalType: TOTAL_TYPE.MAIN,
          totalForPercentage: patients.length,
          intermediateTotals: {},
          currentLevel: 0,
          cardTitles: {},
          forComparison: false,
        };
        const opts = {
          ninetyDaysData: ninetyDaysOptions,
          dynamicCards: questions.map((q) => q.question).filter(Boolean),
          newSavedFilters: [],
          isCheckNewFilters: false,
          objectCustom: {},
          sixtyDaysData: ninetyDaysOptions,
          diffDashboardPatients: diffDashboardPatients || {},
          pageType: page,
          forComparison: false,
          customCombineTabData: [],
          exhustListData: [],
        };
        const result = await getCustomTabsCards(patients, nonCombineTabs, totalFilterData, opts);
        if (!cancelled) {
          setCustomTabCardData(result || {});
        }
      } catch (err) {
        if (!cancelled) {
          setCustomTabCardData({});
        }
      }
    })();
    return () => { cancelled = true; };
  }, [patients, customTabsByPage, currentDashboardPage, ninetyDaysOptions, diffDashboardPatients, questions]);




  useEffect(() => {
    const availableIds = extraColumns.map((c) => c.id);
    const typeChanged = lastTypeRef.current !== filters.type;

    if (availableIds.length === 0) {
      if (filters.type === "all") {
        setSelectedQuestionColumnIds([]);
        setQuestionFilterValues({});
        setQuestionColumnOrder([]);
        lastTypeRef.current = filters.type;
      }
      return;
    }

    setSelectedQuestionColumnIds((prev) => {
      if (typeChanged && filters.type !== "all") {
        return availableIds;
      }

      const stillValid = prev.filter((id) => availableIds.includes(id));
      if (stillValid.length) return stillValid;
      return availableIds;
    });

    setQuestionColumnOrder((prev) => {
      const stillValid = prev.filter((id) => availableIds.includes(id));
      const missing = availableIds.filter((id) => !stillValid.includes(id));
      return [...stillValid, ...missing];
    });

    lastTypeRef.current = filters.type;
  }, [extraColumns, filters.type]);

  const BASE_COLUMN_LABELS = useMemo(
    () => ({
      facility: "Facility",
      firstName: "First Name",
      middleInitial: "M.I.",
      lastName: "Last Name",
      dob: "DOB",
      dateOfADT: "Date of ADT",
      type: "Transfer Type",
    }),
    []
  );
  const searchableBaseColumns = useMemo(
    () =>
      baseColumnOrder
        .filter((id) =>
          selectedBaseColumnIds.includes(id) &&
          (id !== "facility" || showFacilityColumn) &&
          (id !== "type" || filters.type === "all")
        )
        .map((id) => ({ id, label: BASE_COLUMN_LABELS[id] || id })),
    [baseColumnOrder, selectedBaseColumnIds, showFacilityColumn, filters.type, BASE_COLUMN_LABELS]
  );
  const searchableColumns = useMemo(
    () => [...searchableBaseColumns, ...extraColumns.map((c) => ({ id: c.id, label: c.label }))],
    [searchableBaseColumns, extraColumns]
  );

  useEffect(() => {
    if (!extraColumns.length) return;
    setSelectedSearchColumnIds((prev) => {
      const extraIds = extraColumns.map((c) => c.id);
      const toAdd = extraIds.filter((id) => !prev.includes(id));
      if (toAdd.length === 0) return prev;
      return [...prev, ...toAdd];
    });
  }, [extraColumns]);

  useEffect(() => {
    const extraIds = extraColumns.map((c) => c.id);
    const allPossibleIds = [...BASE_COLUMN_IDS, ...extraIds];
    setSelectedSearchColumnIds((prev) => {
      // Keep only IDs that are part of base columns or current extra columns.
      // This allows base columns like "type" to stay selected even if hidden by category.
      const kept = prev.filter((id) => allPossibleIds.includes(id));
      if (kept.length === prev.length) return prev;
      // If none are left, default to all currently searchable columns
      return kept.length ? kept : searchableColumns.map(c => c.id);
    });
  }, [searchableColumns, extraColumns]);

  const visibleExtraColumns = useMemo(() => {
    if (!extraColumns.length || !selectedQuestionColumnIds.length) return [];
    const byId = extraColumns.reduce((acc, col) => {
      acc[col.id] = col;
      return acc;
    }, {});
    return questionColumnOrder
      .filter((id) => selectedQuestionColumnIds.includes(id))
      .map((id) => byId[id])
      .filter(Boolean);
  }, [extraColumns, selectedQuestionColumnIds, questionColumnOrder]);

  const hiddenExtraColumns = useMemo(
    () => extraColumns.filter((col) => !selectedQuestionColumnIds.includes(col.id)),
    [extraColumns, selectedQuestionColumnIds]
  );

  const visibleBaseColumnsInOrder = useMemo(() => {
    return baseColumnOrder.filter(
      (id) =>
        selectedBaseColumnIds.includes(id) &&
        (id !== "facility" || showFacilityColumn) &&
        (id !== "type" || filters.type === "all")
    );
  }, [baseColumnOrder, selectedBaseColumnIds, showFacilityColumn, filters.type]);

  const hiddenBaseColumns = useMemo(() => {
    const hiddenIds = BASE_COLUMN_IDS.filter(
      (id) =>
        !selectedBaseColumnIds.includes(id) &&
        (id !== "facility" || showFacilityColumn) &&
        (id !== "type" || filters.type === "all")
    );
    return hiddenIds.map((id) => ({ id, label: BASE_COLUMN_LABELS[id] || id }));
  }, [selectedBaseColumnIds, showFacilityColumn, filters.type, BASE_COLUMN_LABELS]);

  const extraColumnFilterOptions = useMemo(() => {
    const optionsById = {};
    extraColumns.forEach((col) => {
      const values = new Set();
      patients.forEach((row) => {
        const v = col.render(row);
        const key = v === null || v === undefined || v === "—" ? "" : String(v);
        if (key) values.add(key);
      });
      optionsById[col.id] = Array.from(values).sort((a, b) => a.localeCompare(b));
    });
    return optionsById;
  }, [extraColumns, patients]);

  // Custom tab card items for "Custom tabs filters" section (from getCustomTabsCards, like Hospital)
  const customTabFilterStats = useMemo(() => {
    const page = currentDashboardPage;
    const tabs = page && customTabsByPage?.[page];
    const stats = {};
    if (!page || !tabs || !customTabCardData || Object.keys(customTabCardData).length === 0) {
      return stats;
    }
    Object.entries(customTabCardData).forEach(([accessor, resultData]) => {
      const tab = tabs.find((t) => (t.existingAccessor || t.accessor) === accessor || t.accessor === accessor);
      if (tab?.type === CUSTOM_TAB_TYPE.combineTab) return; // Allow combineTab cards to show their sub-dashboard options
      const label = tab?.title || tab?.label || accessor;
      const arr = Array.isArray(resultData) ? resultData : [];
      const main = arr[0];
      if (!main && arr.length === 0) return;
      let items = [];
      if (Array.isArray(main?.customDataArray) && main.customDataArray.length > 0) {
        items = main.customDataArray
          .filter((i) => i && (i._id || i.label || i.id))
          .map((i) => ({
            value: String(i._id ?? i.id ?? i.label ?? ""),
            label: i.label || i.name || i._id || String(i.id ?? ""),
            total: i.total ?? 0,
            patientIds: i.patientIds || [],
          }));
      } else if (main?.totalByPage && typeof main.totalByPage === "object") {
        items = Object.entries(main.totalByPage).map(([key, entry]) => ({
          value: key,
          label: entry?.label || key,
          total: entry?.total ?? 0,
          patientIds: entry?.patientIds || [],
        }));
      } else {
        const flatItems = arr.filter((i) => i && (i._id !== undefined || i.id !== undefined || i.patientIds));
        if (flatItems.length > 0) {
          items = flatItems.map((i) => ({
            value: String(i._id ?? i.id ?? i.label ?? ""),
            label: i.label || i.name || i._id || String(i.id ?? ""),
            total: i.total ?? 0,
            patientIds: i.patientIds || [],
          }));
        } else if (main && (main.total !== undefined || main.patientIds)) {
          items = [{
            value: String(main._id ?? main.id ?? accessor),
            label: main.label || main.name || label,
            total: main.total ?? 0,
            patientIds: main.patientIds || [],
          }];
        }
      }
      if (items.length > 0) {
        stats[accessor] = { label, items };
      }
    });
    return stats;
  }, [customTabCardData, customTabsByPage, currentDashboardPage]);


  const deriveShiftName = (time) => {
    if (!time || typeof time !== "string") return null;

    // Extract "HH:mm" or "h:mm" portion
    const match = time.match(/(\d{1,2}):(\d{2})/);
    if (!match) return null;

    let hour = parseInt(match[1], 10);
    const minute = parseInt(match[2], 10) || 0;

    // Handle am/pm if present
    const lower = time.toLowerCase();
    const hasAmPm = lower.includes("am") || lower.includes("pm");
    if (hasAmPm) {
      const isPM = lower.includes("pm");
      if (isPM && hour < 12) hour += 12;
      if (!isPM && hour === 12) hour = 0;
    }

    if (Number.isNaN(hour) || hour < 0 || hour > 23) return null;

    const totalMinutes = hour * 60 + minute;

    // Match backend defaults: Morning ~07:00–15:00, Evening ~15:00–23:00, else Night
    const morningStart = 7 * 60;    // 07:00
    const morningEnd = 15 * 60;     // 15:00
    const eveningStart = 15 * 60;   // 15:00
    const eveningEnd = 23 * 60;     // 23:00

    if (totalMinutes > morningStart && totalMinutes < morningEnd) return "Morning";
    if (totalMinutes > eveningStart && totalMinutes < eveningEnd) return "Evening";
    return "Night";
  };

  // All weekdays in order (same options for hospital and community transfer filters)
  const availableDays = useMemo(
    () => ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    []
  );

  const availableShifts = useMemo(
    () => ["Morning", "Evening", "Night"],
    []
  );

  const highlightSearchMatch = (text, colId, rowIndex, limit = null) => {
    const term = (filters.search || "").trim();
    const value = (text || "").toString();

    if (!term || !colId || !selectedSearchColumnIds.includes(colId)) {
      const val = value || "—";
      if (limit && val.length > limit) {
        return (
          <>
            {val.substring(0, limit)}
            <Box component="span" sx={{ ml: 0.1 }}>...</Box>
          </>
        );
      }
      return val;
    }

    const lowerValue = value.toLowerCase();
    const lowerTerm = term.toLowerCase();

    // Find all occurrences of the term in FULL text
    const matches = [];
    let pos = lowerValue.indexOf(lowerTerm);
    while (pos !== -1) {
      matches.push(pos);
      pos = lowerValue.indexOf(lowerTerm, pos + term.length);
    }

    if (matches.length === 0) {
      const val = value || "—";
      if (limit && val.length > limit) {
        return (
          <>
            {val.substring(0, limit)}
            <Box component="span" sx={{ ml: 0.1 }}>...</Box>
          </>
        );
      }
      return val;
    }

    // Dynamic Highlighting check for the "active" match
    const matchesForCol = columnSearchMatches[colId] || [];
    const currentActiveIdx = currentSearchMatchIndices[colId] ?? -1;
    const isActiveInRow = currentActiveIdx >= 0 && matchesForCol[currentActiveIdx] === rowIndex;

    // Global Match check
    const activeGlobal = globalSearchMatchIndex !== -1 ? allGlobalMatches[globalSearchMatchIndex] : null;
    const isGlobalActive = activeGlobal && activeGlobal.rowIndex === rowIndex && activeGlobal.colId === colId;

    const isBlue = isActiveInRow || isGlobalActive;
    const result = [];
    let lastIndex = 0;
    const effectiveLimit = limit !== null ? limit : value.length;
    let hasHiddenMatch = false;

    matches.forEach((matchIndex) => {
      // If any part of the match is hidden (ends after effectiveLimit)
      if (matchIndex + term.length > effectiveLimit) {
        hasHiddenMatch = true;
      }

      // If match starts after the limit, we stop adding highlights to the visible part
      if (matchIndex >= effectiveLimit) return;

      // Add text before match (up to limit)
      if (matchIndex > lastIndex) {
        result.push(value.slice(lastIndex, Math.min(matchIndex, effectiveLimit)));
      }

      // Determine the visible part of the match
      const matchEnd = matchIndex + term.length;
      const visibleEnd = Math.min(matchEnd, effectiveLimit);

      if (visibleEnd > matchIndex) {
        result.push(
          <Box
            key={`${rowIndex}-${colId}-${matchIndex}`}
            component="span"
            sx={{
              backgroundColor: isBlue ? "#3B82F6" : "#FEF3C7",
              color: isBlue ? "#FFFFFF" : "#92400E",
              borderRadius: 0.5,
              px: 0.2,
              transition: "all 0.2s ease",
              boxShadow: isBlue ? "0 0 0 2px #2563EB" : "none",
              fontWeight: isBlue ? 700 : "inherit",
            }}
          >
            {value.slice(matchIndex, visibleEnd)}
          </Box>
        );
      }
      lastIndex = visibleEnd;
    });

    // Add remaining text if any
    if (lastIndex < effectiveLimit) {
      result.push(value.slice(lastIndex, effectiveLimit));
    }

    // Append Ellipsis if truncated
    if (limit && value.length > limit) {
      result.push(
        <span
          key="ellipsis"
          style={{
            backgroundColor: hasHiddenMatch ? (isBlue ? "#3B82F6" : "#FEF3C7") : "transparent",
            color: hasHiddenMatch ? (isBlue ? "#FFFFFF" : "#92400E") : "inherit",
            borderRadius: "2px",
            padding: "0 2px",
            marginLeft: "2px",
            fontWeight: hasHiddenMatch ? 700 : "inherit",
            transition: "all 0.2s ease",
            display: "inline-block",
            lineHeight: 1
          }}
        >
          ...
        </span>
      );
    }

    return <>{result}</>;
  };

  const appliedColumnFilters = useMemo(() => {
    const items = [];

    // Base column filters (facility, firstName, lastName, dob, dateOfADT)
    if ((baseColumnFilterValues.facility || "").trim()) {
      items.push(`Facility contains "${baseColumnFilterValues.facility.trim()}"`);
    }
    if ((baseColumnFilterValues.firstName || "").trim()) {
      items.push(`First Name contains "${baseColumnFilterValues.firstName.trim()}"`);
    }
    if ((baseColumnFilterValues.lastName || "").trim()) {
      items.push(`Last Name contains "${baseColumnFilterValues.lastName.trim()}"`);
    }
    if (baseColumnFilterValues.dobFrom || baseColumnFilterValues.dobTo) {
      const range = [baseColumnFilterValues.dobFrom, baseColumnFilterValues.dobTo].filter(Boolean).join(" to ");
      items.push(`DOB: ${range}`);
    }
    if (baseColumnFilterValues.dateOfADTFrom || baseColumnFilterValues.dateOfADTTo) {
      const range = [baseColumnFilterValues.dateOfADTFrom, baseColumnFilterValues.dateOfADTTo].filter(Boolean).join(" to ");
      items.push(`Date of ADT: ${range}`);
    }

    // Column (question) filters from column header filter icon
    Object.entries(questionFilterValues).forEach(([colId, vals]) => {
      if (Array.isArray(vals) && vals.length > 0) {
        const col = extraColumns.find((c) => c.id === colId);
        const label = col ? col.label : colId;
        items.push(`${label}: ${vals.join(", ")}`);
      }
    });

    // Custom tab filters
    Object.entries(customTabFilterValues).forEach(([accessor, vals]) => {
      if (Array.isArray(vals) && vals.length > 0) {
        const section = customTabFilterStats[accessor];
        const label = section?.label ?? accessor;
        items.push(`Custom: ${label}: ${vals.join(", ")}`);
      }
    });

    return items;
  }, [
    baseColumnFilterValues,
    questionFilterValues,
    customTabFilterValues,
    customTabFilterStats,
    extraColumns,
  ]);

  const appliedAdvancedFilters = useMemo(() => {
    const items = [...appliedColumnFilters];

    if (filters.type === "hospitalTransfer") {
      const transferArr = Array.isArray(filters.transferType) ? filters.transferType : [];
      if (transferArr.length > 0) {
        const labels = transferArr.map((v) =>
          v === "plannedHospitalTransfer" ? (HOSPITAL_CARDS_LABELS.plannedHospitalTransfer || "Planned Transfers")
            : v === "unplannedHospitalTransfer" ? (HOSPITAL_CARDS_LABELS.unplannedHospitalTransfer || "Unplanned Transfers") : v
        );
        items.push(`Planned Transfers / Unplanned Transfers: ${labels.join(", ")}`);
      }

      const hospArr = Array.isArray(hospitalizationFilter) ? hospitalizationFilter : [];
      if (hospArr.length > 0) {
        const labels = hospArr.map((v) => (v === "new" ? "New" : "Re-Hospitalizations"));
        items.push(`Hospitalizations: ${labels.join(", ")}`);
      }

      const dcerArr = Array.isArray(dcerFilter) ? dcerFilter : [];
      if (dcerArr.length > 0) items.push(`DC / ER: ${dcerArr.join(", ")}`);

      const retArr = Array.isArray(returnsFilter) ? returnsFilter : [];
      if (retArr.length > 0) {
        const labels = retArr.map((v) => (v === "returned" ? "Returned" : "Didn't Return"));
        items.push(`Returns: ${labels.join(", ")}`);
      }

      if (Array.isArray(ninetyDaysFilter) && ninetyDaysFilter.length > 0) {
        const labels = ninetyDaysFilter
          .map((id) => {
            const bucket = ninetyDaysOptions.find(
              (b) => String(b._id) === String(id)
            );
            return bucket ? bucket.label : id;
          })
          .filter(Boolean);
        if (labels.length) {
          items.push(`90 Days analysis: ${labels.join(", ")}`);
        }
      }

      if (Array.isArray(daysFilter) && daysFilter.length > 0) {
        items.push(`Transfers per day of the week: ${daysFilter.join(", ")}`);
      }

      if (Array.isArray(shiftFilter) && shiftFilter.length > 0) {
        items.push(`Transfers per shift: ${shiftFilter.join(", ")}`);
      }
    }

    const communityArr = Array.isArray(filters.transferType) ? filters.transferType : [];
    if (filters.type === "communityTransfer" && communityArr.length > 0) {
      const labels = communityArr.map((v) => COMMUNITY_CARD_LABELS[v] || v);
      items.push(`Community Transfer Type: ${labels.join(", ")}`);
    }
    if (filters.type === "communityTransfer") {
      const retArr = Array.isArray(returnsFilter) ? returnsFilter : [];
      if (retArr.length > 0) {
        const labels = retArr.map((v) => (v === "returned" ? "Returned" : "Didn't Return"));
        items.push(`Returned / Didn't Return: ${labels.join(", ")}`);
      }
      if (Array.isArray(ninetyDaysFilter) && ninetyDaysFilter.length > 0) {
        const labels = ninetyDaysFilter
          .map((id) => {
            const bucket = ninetyDaysOptions.find((b) => String(b._id) === String(id));
            return bucket ? bucket.label : id;
          })
          .filter(Boolean);
        if (labels.length) items.push(`${COMMUNITY_CARD_LABELS.sixtyDaysData || "90 Days analysis"}: ${labels.join(", ")}`);
      }
      if (Array.isArray(daysFilter) && daysFilter.length > 0) {
        items.push(`Transfers per day of the week: ${daysFilter.join(", ")}`);
      }
      if (Array.isArray(shiftFilter) && shiftFilter.length > 0) {
        items.push(`Transfers per shift: ${shiftFilter.join(", ")}`);
      }
    }

    if (filters.type === "deceased") {
      if (Array.isArray(ninetyDaysFilter) && ninetyDaysFilter.length > 0) {
        const labels = ninetyDaysFilter
          .map((id) => {
            const bucket = ninetyDaysOptions.find((b) => String(b._id) === String(id));
            return bucket ? bucket.label : id;
          })
          .filter(Boolean);
        if (labels.length) items.push(`90 Days analysis: ${labels.join(", ")}`);
      }
      if (Array.isArray(daysFilter) && daysFilter.length > 0) {
        items.push(`Transfers per day of the week: ${daysFilter.join(", ")}`);
      }
    }

    const admArr = Array.isArray(filters.admissionType) ? filters.admissionType : [];
    if (filters.type === "admission" && admArr.length > 0) {
      const labels = admArr.map((v) => (v === "admission" ? "Admission" : v === "reAdmission" ? "Re-Admission" : v));
      items.push(`Type: ${labels.join(", ")}`);
    }
    if (filters.type === "admission" && Array.isArray(daysFilter) && daysFilter.length > 0) {
      items.push(`Transfers per day of the week: ${daysFilter.join(", ")}`);
    }

    return items;
  }, [
    appliedColumnFilters,
    filters.type,
    filters.transferType,
    filters.admissionType,
    hospitalizationFilter,
    dcerFilter,
    returnsFilter,
    ninetyDaysFilter,
    ninetyDaysOptions,
    daysFilter,
    shiftFilter,
  ]);

  const filteredPatients = useMemo(() => {
    const activeQuestionFilters = Object.entries(questionFilterValues).filter(
      ([, vals]) => Array.isArray(vals) && vals.length > 0
    );
    let result = patients;

    // Local search across selected columns only
    const searchTerm = (filters.search || "").trim().toLowerCase();
    if (searchTerm) {
      if (selectedSearchColumnIds.length === 0) return [];
      const extraColsById = extraColumns.reduce((acc, c) => {
        acc[c.id] = c;
        return acc;
      }, {});
      result = result.filter((row) => {
        return selectedSearchColumnIds.some((colId) => {
          let value = "";
          if (colId === "firstName") value = (row.firstName || "").toString();
          else if (colId === "lastName") value = (row.lastName || "").toString();
          else if (colId === "middleInitial") value = (row.middleInitial || "").toString();
          else if (colId === "type") value = (formatTypeLabel(row) || "").toString();
          else if (colId === "dob" || colId === "DOB") value = row.DOB ? toDisplayTime(row.DOB, "MM/DD/YYYY") : "";
          else if (colId === "facility") value = (_.get(row, "facilityId.name") || "").toString();
          else if (colId === "dateOfADT") value = row.dateOfADT ? toDisplayTime(row.dateOfADT, "MM/DD/YYYY") : "";
          else if (extraColsById[colId]) value = (extraColsById[colId].render(row) ?? "").toString();
          return value.toLowerCase().includes(searchTerm);
        });
      });
    }

    // Base column filters (facility, firstName, lastName, dob, dateOfADT)
    if ((baseColumnFilterValues.facility || "").trim()) {
      const term = baseColumnFilterValues.facility.trim().toLowerCase();
      result = result.filter((row) => (_.get(row, "facilityId.name") || "").toString().toLowerCase().includes(term));
    }
    if ((baseColumnFilterValues.firstName || "").trim()) {
      const term = baseColumnFilterValues.firstName.trim().toLowerCase();
      result = result.filter((row) => (row.firstName || "").toLowerCase().includes(term));
    }
    if ((baseColumnFilterValues.lastName || "").trim()) {
      const term = baseColumnFilterValues.lastName.trim().toLowerCase();
      result = result.filter((row) => (row.lastName || "").toLowerCase().includes(term));
    }
    if (baseColumnFilterValues.dobFrom || baseColumnFilterValues.dobTo) {
      result = result.filter((row) => {
        const rowDob = row.DOB ? moment(row.DOB).format("YYYY-MM-DD") : "";
        if (!rowDob) return false;
        if (baseColumnFilterValues.dobFrom && rowDob < baseColumnFilterValues.dobFrom) return false;
        if (baseColumnFilterValues.dobTo && rowDob > baseColumnFilterValues.dobTo) return false;
        return true;
      });
    }
    if (baseColumnFilterValues.dateOfADTFrom || baseColumnFilterValues.dateOfADTTo) {
      result = result.filter((row) => {
        const rowDate = row.dateOfADT ? moment(row.dateOfADT).format("YYYY-MM-DD") : "";
        if (!rowDate) return false;
        if (baseColumnFilterValues.dateOfADTFrom && rowDate < baseColumnFilterValues.dateOfADTFrom) return false;
        if (baseColumnFilterValues.dateOfADTTo && rowDate > baseColumnFilterValues.dateOfADTTo) return false;
        return true;
      });
    }
    if ((baseColumnFilterValues.type || "").trim()) {
      const term = baseColumnFilterValues.type.trim().toLowerCase();
      result = result.filter((row) => (formatTypeLabel(row) || "").toLowerCase().includes(term));
    }
    if ((baseColumnFilterValues.notes || "").trim()) {
      const term = baseColumnFilterValues.notes.trim().toLowerCase();
      result = result.filter((row) => {
        let val = row.notes || row.note;
        if (val && typeof val === "object") {
          val = val.value || val.label;
        }
        return (val || "").toString().toLowerCase().includes(term);
      });
    }

    // Local advanced hospital/community transfer filters (multi-select)
    const transferArr = Array.isArray(filters.transferType) ? filters.transferType : [];
    if (filters.type === "hospitalTransfer" && transferArr.length > 0) {
      result = result.filter((row) => transferArr.includes(row.transferType));
    }
    if (filters.type === "communityTransfer" && transferArr.length > 0) {
      result = result.filter((row) => transferArr.includes(row.transferType));
    }

    // Local admission type filter (multi-select) — support both admissionType and type (admission/readmission)
    const admArr = Array.isArray(filters.admissionType) ? filters.admissionType : [];
    if (filters.type === "admission" && admArr.length > 0) {
      result = result.filter((row) => {
        const t = row.admissionType ?? row.type;
        if (t === "readmission") return admArr.includes("reAdmission");
        if (t === "admission") return admArr.includes("admission");
        return admArr.includes(t);
      });
    }

    // Hospitalization filter (multi-select: hospital transfers only)
    const hospArr = Array.isArray(hospitalizationFilter) ? hospitalizationFilter : [];
    if (filters.type === "hospitalTransfer" && hospArr.length > 0) {
      result = result.filter((row) =>
        (hospArr.includes("new") && row.reHospitalization === false) ||
        (hospArr.includes("re") && row.reHospitalization === true)
      );
    }

    // DC / ER filter (multi-select: hospital transfers only)
    const dcerArr = Array.isArray(dcerFilter) ? dcerFilter : [];
    if (filters.type === "hospitalTransfer" && dcerArr.length > 0) {
      result = result.filter((row) =>
        (dcerArr.includes("DC") && row.wasAdmitted === true) ||
        (dcerArr.includes("ER") && row.wasAdmitted === false)
      );
    }

    // Returns filter (multi-select: hospital and community transfers)
    const retArr = Array.isArray(returnsFilter) ? returnsFilter : [];
    if ((filters.type === "hospitalTransfer" || filters.type === "communityTransfer") && retArr.length > 0) {
      result = result.filter((row) =>
        (retArr.includes("returned") && row.wasReturned === true) ||
        (retArr.includes("didntReturn") && row.wasReturned === false)
      );
    }

    // 90-days filter (multi)
    if (
      (filters.type === "hospitalTransfer" ||
        filters.type === "communityTransfer" ||
        filters.type === "deceased") &&
      Array.isArray(ninetyDaysFilter) &&
      ninetyDaysFilter.length > 0
    ) {
      result = result.filter((row) => {
        const buckets = Array.isArray(row.ninetyDaysBuckets)
          ? row.ninetyDaysBuckets.map((id) => id.toString())
          : [];
        return ninetyDaysFilter.some((bid) => buckets.includes(bid.toString()));
      });
    }

    // Day-of-week filter (multi) — normalize short names (e.g. "Fri") to full ("Friday")
    if (
      (filters.type === "hospitalTransfer" ||
        filters.type === "communityTransfer" ||
        filters.type === "deceased" ||
        filters.type === "admission") &&
      Array.isArray(daysFilter) &&
      daysFilter.length > 0
    ) {
      result = result.filter((row) => {
        const rawDay = row.day;
        let dayName =
          rawDay ||
          (row.dateOfADT ? moment(row.dateOfADT).format("dddd") : null);
        if (dayName && DAY_SHORT_TO_FULL[dayName]) dayName = DAY_SHORT_TO_FULL[dayName];
        if (!dayName) return false;
        return daysFilter.includes(dayName);
      });
    }

    // Shift filter (multi)
    if ((filters.type === "hospitalTransfer" || filters.type === "communityTransfer") && Array.isArray(shiftFilter) && shiftFilter.length > 0) {
      result = result.filter((row) => {
        const raw = row.shiftName || deriveShiftName(row.transferTime);
        if (!raw) return false;
        const name = raw.toString().toLowerCase();
        return shiftFilter.some((val) => {
          const v = val.toString().toLowerCase();
          if (v === "morning") return name.includes("morning");
          if (v === "evening") return name.includes("evening");
          if (v === "night") return name.includes("night");
          return name === v;
        });
      });
    }

    // Question-based column filters
    if (activeQuestionFilters.length) {
      result = result.filter((row) =>
        activeQuestionFilters.every(([colId, selectedValues]) => {
          const col = extraColumns.find((c) => c.id === colId);
          if (!col) return true;
          const v = col.render(row);
          const key = v === null || v === undefined || v === "—" ? "" : String(v);
          if (!key) return false;
          return selectedValues.includes(key);
        })
      );
    }

    // Custom tab filters (filter by patientIds from selected card items)
    const activeCustomTabFilters = Object.entries(customTabFilterValues).filter(
      ([, ids]) => Array.isArray(ids) && ids.length > 0
    );
    if (activeCustomTabFilters.length > 0 && customTabCardData) {
      activeCustomTabFilters.forEach(([accessor, selectedIds]) => {
        const cardData = customTabCardData[accessor];
        const arr = Array.isArray(cardData) ? cardData : [];
        const main = arr[0];
        const allowedPatientIds = new Set();
        const normalizeId = (id) => (id == null ? "" : String(id).trim());
        const addPatientIds = (ids) => (ids || []).forEach((id) => {
          const sid = normalizeId(id);
          if (sid) allowedPatientIds.add(sid);
        });
        const addFromItem = (i) => {
          addPatientIds(i.patientIds);
          if (!(i.patientIds && i.patientIds.length) && (i.Ids || i.otherDashboardIds)) {
            addPatientIds(i.Ids || i.otherDashboardIds);
          }
        };

        if (main?.customDataArray) {
          main.customDataArray
            .filter((i) => i && selectedIds.includes(normalizeId(i._id ?? i.id ?? i.label ?? "")))
            .forEach((i) => addFromItem(i));
        } else if (main?.totalByPage) {
          Object.entries(main.totalByPage).forEach(([key, entry]) => {
            if (selectedIds.includes(normalizeId(key)) && entry) addFromItem(entry);
          });
        } else {
          const flatItems = arr.filter((i) => i && (i.patientIds || i.Ids || i.otherDashboardIds || i._id !== undefined || i.id !== undefined));
          flatItems
            .filter((i) => selectedIds.includes(normalizeId(i._id ?? i.id ?? i.label ?? "")))
            .forEach((i) => addFromItem(i));

          // If still no IDs and it's a single main card (like hospitalizations), check main card itself
          if (allowedPatientIds.size === 0 && arr.length === 1 && main) {
            if (selectedIds.includes(normalizeId(main._id ?? main.id ?? accessor))) {
              addFromItem(main);
            }
          }
        }

        // Apply filter even if set is empty (meaning no matches for this selected filter)
        // This ensures the filter is EXCLUSIVE (AND logic)
        result = result.filter((row) => {
          const rid = normalizeId(row._id ?? row.id ?? "");
          const refId = normalizeId(row.refPatientId ?? "");
          return (rid && allowedPatientIds.has(rid)) || (refId && allowedPatientIds.has(refId));
        });
      });
    }

    return result;
  }, [
    patients,
    questionFilterValues,
    customTabFilterValues,
    customTabCardData,
    extraColumns,
    baseColumnFilterValues,
    filters.search,
    filters.type,
    filters.transferType,
    filters.admissionType,
    hospitalizationFilter,
    dcerFilter,
    returnsFilter,
    ninetyDaysFilter,
    daysFilter,
    shiftFilter,
    selectedSearchColumnIds,
  ]);

  const sortedPatients = useMemo(() => {
    if (!sortBy) return filteredPatients;

    const extraColsById = extraColumns.reduce((acc, c) => {
      acc[c.id] = c;
      return acc;
    }, {});

    const compare = (aVal, bVal, asDate = false) => {
      if (asDate) {
        const dA = aVal ? new Date(aVal).getTime() : 0;
        const dB = bVal ? new Date(bVal).getTime() : 0;
        return dA - dB;
      }
      const aStr = (aVal || "").toString().toLowerCase();
      const bStr = (bVal || "").toString().toLowerCase();
      if (aStr < bStr) return -1;
      if (aStr > bStr) return 1;
      return 0;
    };

    const sorted = [...filteredPatients].sort((a, b) => {
      let resultCompare = 0;
      if (sortBy === "facility") {
        resultCompare = compare(_.get(a, "facilityId.name"), _.get(b, "facilityId.name"));
      } else if (sortBy === "lastName") {
        resultCompare = compare(a.lastName, b.lastName);
      } else if (sortBy === "firstName") {
        resultCompare = compare(a.firstName, b.firstName);
      } else if (sortBy === "middleInitial") {
        resultCompare = compare(a.middleInitial, b.middleInitial);
      } else if (sortBy === "type") {
        resultCompare = compare(a.type, b.type);
      } else if (sortBy === "dateOfADT") {
        resultCompare = compare(a.dateOfADT, b.dateOfADT, true);
      } else if (sortBy === "dob" || sortBy === "DOB") {
        resultCompare = compare(a.DOB, b.DOB, true);
      } else if (extraColsById[sortBy]) {
        const col = extraColsById[sortBy];
        const valA = col.render ? col.render(a) : _.get(a, sortBy);
        const valB = col.render ? col.render(b) : _.get(b, sortBy);
        const isDate = col.isDate || col.label?.toLowerCase().includes("date");
        resultCompare = compare(valA, valB, isDate);
      }
      return sortDirection === "asc" ? resultCompare : -resultCompare;
    });

    return sorted;
  }, [filteredPatients, sortBy, sortDirection, extraColumns]);

  const handleDownloadExcel = useCallback(async (options = {}) => {
    const returnBlob = options && options.returnBlob === true;
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Residents List", { views: [{ state: "frozen", ySplit: 1 }] });

    // Header row: visible base columns in order, then Middle Initial, Transfer Type (if), then visible extra columns
    const headers = visibleBaseColumnsInOrder.map((id) => BASE_COLUMN_LABELS[id] || id);
    headers.push("Middle Initial");
    if (filters.type === "all") {
      headers.push("Transfer Type");
    }
    visibleExtraColumns.forEach((col) => headers.push(col.label || col.id));
    const headerRow = sheet.addRow(headers);
    styleHeaderRow(headerRow);

    // Data rows (plain text, no HTML)
    const toPlain = (v) => {
      if (v == null || v === undefined) return "";
      const s = typeof v === "string" ? v : String(v);
      return s.replace(/<[^>]*>/g, "").trim() || s;
    };

    const sortedForExport = [...filteredPatients].sort((a, b) => {
      const compare = (aVal, bVal, asDate = false) => {
        if (asDate) {
          const dA = aVal ? new Date(aVal).getTime() : 0;
          const dB = bVal ? new Date(bVal).getTime() : 0;
          return dA - dB;
        }
        const aStr = (aVal || "").toString().toLowerCase();
        const bStr = (bVal || "").toString().toLowerCase();
        if (aStr < bStr) return -1;
        if (aStr > bStr) return 1;
        return 0;
      };

      const extraColsById = visibleExtraColumns.reduce((acc, c) => {
        acc[c.id] = c;
        return acc;
      }, {});

      let result = 0;
      if (sortBy === "facility") result = compare(_.get(a, "facilityId.name"), _.get(b, "facilityId.name"));
      else if (sortBy === "lastName") result = compare(a.lastName, b.lastName);
      else if (sortBy === "firstName") result = compare(a.firstName, b.firstName);
      else if (sortBy === "middleInitial") result = compare(a.middleInitial, b.middleInitial);
      else if (sortBy === "type") result = compare(a.type, b.type);
      else if (sortBy === "dateOfADT") result = compare(a.dateOfADT, b.dateOfADT, true);
      else if (sortBy === "dob" || sortBy === "DOB") result = compare(a.DOB, b.DOB, true);
      else if (extraColsById[sortBy]) {
        const col = extraColsById[sortBy];
        const valA = col.render ? col.render(a) : _.get(a, sortBy);
        const valB = col.render ? col.render(b) : _.get(b, sortBy);
        const isDate = col.isDate || col.label?.toLowerCase().includes("date");
        result = compare(valA, valB, isDate);
      }
      return sortDirection === "asc" ? result : -result;
    });

    for (const row of sortedForExport) {
      const cells = [];
      visibleBaseColumnsInOrder.forEach((baseId) => {
        if (baseId === "facility") cells.push(toPlain(_.get(row, "facilityId.name", "—")));
        else if (baseId === "firstName") cells.push(toPlain(row.firstName ?? "—"));
        else if (baseId === "lastName") cells.push(toPlain(row.lastName ?? "—"));
        else if (baseId === "dob") cells.push(row.DOB ? toDisplayTime(row.DOB, "MM/DD/YYYY") : "—");
        else if (baseId === "dateOfADT") cells.push(row.dateOfADT ? toDisplayTime(row.dateOfADT, "MM/DD/YYYY") : "—");
      });
      cells.push(toPlain(row.middleInitial ?? "—"));
      if (filters.type !== "hospitalTransfer" && filters.type !== "communityTransfer" && filters.type !== "deceased") {
        cells.push(toPlain(formatTypeLabel(row)));
      }
      visibleExtraColumns.forEach((col) => {
        const v = col.render ? col.render(row) : "—";
        cells.push(toPlain(v != null && v !== undefined ? v : "—"));
      });
      sheet.addRow(cells);
    }

    autoFitColumns(sheet, { headerRowNumber: headerRow.number });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    if (returnBlob) return blob;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `residence-list-${filters.fromDate || ""}-to-${filters.toDate || ""}.xlsx`;
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
  }, [
    filters.fromDate,
    filters.toDate,
    filters.type,
    filteredPatients,
    visibleBaseColumnsInOrder,
    visibleExtraColumns,
    sortBy,
    sortDirection,
    BASE_COLUMN_LABELS,
  ]);

  const {
    shareReportState,
    users,
    availableIntegrations,
    accountId: accountIdFromHook,
    facilityId: facilityIdFromHook,
    handleOnClickShareReport,
    handleCloseShareReport,
    handleShareReport,
  } = useShareReport({
    downloadPdf: null,
    generateExcel: handleDownloadExcel,
    excelReportData: {},
    pageType: "residenceList",
  });

  const baseColumnCount =
    visibleBaseColumnsInOrder.length +
    visibleExtraColumns.length;

  const tableColumnsForSearch = useMemo(() => {
    const items = [];
    let idx = 0;
    if (filters.type !== "all") {
      items.push({ id: "action", label: "Action", columnIndex: -1 });
    }
    visibleBaseColumnsInOrder.forEach((id) => {
      items.push({ id: `base-${id}`, label: BASE_COLUMN_LABELS[id] || id, columnIndex: idx++ });
    });
    visibleExtraColumns.forEach((col) => {
      items.push({ id: col.id, label: col.label || col.id, columnIndex: idx++ });
    });
    return items;
  }, [visibleBaseColumnsInOrder, visibleExtraColumns, BASE_COLUMN_LABELS, filters.type]);

  const filteredColumnsForSearch = useMemo(() => {
    if (!searchColumnQuery.trim()) return tableColumnsForSearch;
    const q = searchColumnQuery.trim().toLowerCase();
    return tableColumnsForSearch.filter((item) => item.label.toLowerCase().includes(q));
  }, [tableColumnsForSearch, searchColumnQuery]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    };
  }, []);

  const handleScrollToColumn = useCallback((columnIndex) => {
    setSearchColumnAnchorEl(null);
    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    setHighlightedColumnIndex(columnIndex);
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedColumnIndex(null);
      highlightTimeoutRef.current = null;
    }, 2500);
    const container = tableContainerRef.current;
    if (!container) return;
    setTimeout(() => {
      const cell = container.querySelector(`[data-column-index="${columnIndex}"]`);
      if (!cell) return;
      const containerRect = container.getBoundingClientRect();
      const cellRect = cell.getBoundingClientRect();
      const scrollDelta = cellRect.left - containerRect.left;

      // Calculate frozen column width to subtract from scroll position - Only if not "all"
      const isAllType = filters.type === 'all';
      const frozenWidth = isAllType ? 0 : ((filters.type !== 'all' ? (isMobile ? 40 : 80) : 0) + (showFacilityColumn ? (isMobile ? 100 : 140) : 0) + (isMobile ? 200 : 280));

      container.scrollTo({
        left: container.scrollLeft + scrollDelta - frozenWidth - 10,
        behavior: 'smooth'
      });
    }, 100);
  }, [filters.type, showFacilityColumn, isMobile]);

  // ── Per-column search match lists (indices into sortedPatients) ──────────────
  const columnSearchMatches = useMemo(() => {
    const term = (filters.search || "").trim().toLowerCase();
    if (!term || selectedSearchColumnIds.length === 0) return {};
    const extraColsById = extraColumns.reduce((acc, c) => { acc[c.id] = c; return acc; }, {});
    const result = {};
    selectedSearchColumnIds.forEach((colId) => {
      const matchingIndices = [];
      filteredPatients.forEach((row, idx) => {
        let value = "";
        if (colId === "firstName") value = (row.firstName || "").toString();
        else if (colId === "lastName") value = (row.lastName || "").toString();
        else if (colId === "middleInitial") value = (row.middleInitial || "").toString();
        else if (colId === "type") {
          const lbl = row.type === "transfer" && row.transferType ? row.transferType : (row.type || "");
          value = lbl.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (s) => s.toUpperCase());
        }
        else if (colId === "dob" || colId === "DOB") value = row.DOB ? toDisplayTime(row.DOB, "MM/DD/YYYY") : "";
        else if (colId === "facility") value = (_.get(row, "facilityId.name") || "").toString();
        else if (colId === "dateOfADT") value = row.dateOfADT ? toDisplayTime(row.dateOfADT, "MM/DD/YYYY") : "";
        else if (extraColsById[colId]) value = (extraColsById[colId].render(row) ?? "").toString();
        if (value.toLowerCase().includes(term)) matchingIndices.push(idx);
      });
      if (matchingIndices.length > 0) result[colId] = matchingIndices;
    });
    return result;
  }, [filters.search, selectedSearchColumnIds, filteredPatients, extraColumns]);

  const allGlobalMatches = useMemo(() => {
    const term = (filters.search || "").trim().toLowerCase();
    if (!term || !selectedSearchColumnIds.length || !filteredPatients.length || !tableColumnsForSearch.length) return [];

    // Visual order of columns that are currently being searched
    const order = tableColumnsForSearch
      .filter(c => selectedSearchColumnIds.includes(c.id.replace('base-', '')))
      .map(c => ({ id: c.id.replace('base-', ''), visualIndex: c.columnIndex }));

    const matches = [];
    order.forEach((col) => {
      // For each column, check all rows
      filteredPatients.forEach((row, rowIndex) => {
        if (columnSearchMatches[col.id] && columnSearchMatches[col.id].includes(rowIndex)) {
          matches.push({ rowIndex, colId: col.id, visualIndex: col.visualIndex });
        }
      });
    });
    return matches;
  }, [filters.search, filteredPatients, selectedSearchColumnIds, tableColumnsForSearch, columnSearchMatches]);

  // Reset per-column navigation indices whenever search term changes
  useEffect(() => {
    setCurrentSearchMatchIndices({});
    setGlobalSearchMatchIndex(-1);
  }, [filters.search]);

  const handleGlobalSearchNavigation = useCallback((direction) => {
    if (allGlobalMatches.length === 0) return;
    setGlobalSearchMatchIndex((prev) => {
      let next;
      if (direction === "next") {
        next = prev + 1 >= allGlobalMatches.length ? 0 : prev + 1;
      } else {
        next = prev - 1 < 0 ? allGlobalMatches.length - 1 : prev - 1;
      }

      const { rowIndex, visualIndex } = allGlobalMatches[next];
      const container = tableContainerRef.current;
      if (container) {
        requestAnimationFrame(() => {
          // 1. Vertical Scroll
          const row = container.querySelector(`tbody tr:nth-child(${rowIndex + 1})`);
          if (row) {
            const thead = container.querySelector("thead");
            const headerHeight = thead ? thead.getBoundingClientRect().height : 0;
            const containerTop = container.getBoundingClientRect().top;
            const rowTop = row.getBoundingClientRect().top;
            const currentScrollTop = container.scrollTop;
            const targetScrollTop = currentScrollTop + (rowTop - containerTop) - headerHeight - 4;
            container.scrollTo({ top: targetScrollTop, behavior: "smooth" });
          }

          // 2. Horizontal Scroll
          const cell = container.querySelector(`[data-column-index="${visualIndex}"]`);
          if (cell) {
            const containerRect = container.getBoundingClientRect();
            const cellRect = cell.getBoundingClientRect();
            const scrollDelta = cellRect.left - containerRect.left;

            // Calculate frozen column width to subtract from scroll position - Only if not "all"
            const isAllType = filters.type === 'all';
            const frozenWidth = isAllType ? 0 : ((filters.type !== 'all' ? (isMobile ? 40 : 80) : 0) + (showFacilityColumn ? (isMobile ? 100 : 140) : 0) + (isMobile ? 200 : 280));

            container.scrollLeft += scrollDelta - frozenWidth - 10;
          }
        });
      }
      return next;
    });
  }, [allGlobalMatches, tableContainerRef, filters.type, showFacilityColumn, isMobile]);

  const handleScrollToMatch = useCallback((colId, direction) => {
    const matches = columnSearchMatches[colId];
    if (!matches || matches.length === 0) return;
    setCurrentSearchMatchIndices((prev) => {
      const current = prev[colId] ?? -1;
      let next;
      if (direction === "next") {
        next = current + 1 >= matches.length ? 0 : current + 1;
      } else {
        next = current - 1 < 0 ? matches.length - 1 : current - 1;
      }
      // Scroll to the row
      const rowIndex = matches[next];
      const container = tableContainerRef.current;
      if (container) {
        requestAnimationFrame(() => {
          const row = container.querySelector(`tbody tr:nth-child(${rowIndex + 1})`);
          if (row) {
            // Get sticky header height
            const thead = container.querySelector("thead");
            const headerHeight = thead ? thead.getBoundingClientRect().height : 0;
            const containerTop = container.getBoundingClientRect().top;
            const rowTop = row.getBoundingClientRect().top;
            const currentScrollTop = container.scrollTop;
            const targetScrollTop = currentScrollTop + (rowTop - containerTop) - headerHeight - 4;
            container.scrollTo({ top: targetScrollTop, behavior: "smooth" });
          }

          // 2. Horizontal Scroll
          const cell = container.querySelector(`[id="${colId}_cell_${rowIndex}"]`);
          if (cell) {
            const containerRect = container.getBoundingClientRect();
            const cellRect = cell.getBoundingClientRect();
            const scrollDelta = cellRect.left - containerRect.left;

            // Calculate frozen column width to subtract from scroll position - Only if not "all"
            const isAllType = filters.type === 'all';
            const frozenWidth = isAllType ? 0 : ((filters.type !== 'all' ? (isMobile ? 40 : 80) : 0) + (showFacilityColumn ? (isMobile ? 100 : 140) : 0) + (isMobile ? 200 : 280));

            container.scrollLeft += (scrollDelta - frozenWidth - 10);
          }
        });
      }
      return { ...prev, [colId]: next };
    });
  }, [columnSearchMatches, tableContainerRef, filters.type, showFacilityColumn, isMobile]);


  const renderAdvancedFiltersContent = () => {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
        {/* Header */}
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            bgcolor: "#f9fafb",
          }}
        >
          {appliedColumnFilters.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {appliedColumnFilters.map((filter) => (
                <Box
                  key={filter}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    bgcolor: "rgba(37, 99, 235, 0.08)",
                    color: PRIMARY,
                    border: "1px solid rgba(37, 99, 235, 0.2)",
                    borderRadius: 1.5,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                  }}
                >
                  {filter}
                </Box>
              ))}
            </Box>
          )}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: PRIMARY,
                  color: "#fff",
                  boxShadow: "0 2px 4px rgba(37, 99, 235, 0.2)"
                }}
              >
                <FilterAltIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#111827" }}>
                  Advanced Filters
                </Typography>
                {appliedAdvancedFilters.length > 0 && (
                  <Typography sx={{ fontSize: "0.75rem", color: "#6b7280", fontWeight: 500 }}>
                    {appliedAdvancedFilters.length} active filters applied
                  </Typography>
                )}
              </Box>
            </Box>
            {appliedAdvancedFilters.length > 0 && (
              <Button
                size="small"
                onClick={handleResetAdvancedFilters}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: PRIMARY,
                  '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.04)' }
                }}
              >
                Reset All
              </Button>
            )}
          </Box>
        </Box>

        {/* Body: search + expandable filter sections, dot bullets, multi-select */}
        <Box sx={{ overflowY: "auto", flex: 1, minWidth: 280 }}>
          <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
            <TextField
              size="small"
              placeholder="Search filters..."
              value={advancedFiltersQuery}
              onChange={(e) => setAdvancedFiltersQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: 1, bgcolor: "#f9fafb", "& fieldset": { borderColor: "#E5E7EB" } },
              }}
              sx={{ "& .MuiInputBase-root": { fontSize: "0.875rem" } }}
              fullWidth
            />
          </Box>
          {(() => {
            const allSections = [];
            if (filters.type === "hospitalTransfer") {
              allSections.push(
                {
                  key: "transferType",
                  label: "Planned / Unplanned Transfer",
                  options: [
                    { value: "plannedHospitalTransfer", label: HOSPITAL_CARDS_LABELS.plannedHospitalTransfer || "Planned Transfers" },
                    { value: "unplannedHospitalTransfer", label: HOSPITAL_CARDS_LABELS.unplannedHospitalTransfer || "Unplanned Transfers" },
                  ],
                  selected: (v) => (Array.isArray(filters.transferType) ? filters.transferType : []).includes(v),
                  toggle: (v) => {
                    const arr = Array.isArray(filters.transferType) ? filters.transferType : [];
                    setFilters((f) => ({ ...f, transferType: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] }));
                  },
                },
                {
                  key: "hospitalization",
                  label: "Hospitalizations",
                  options: [
                    { value: "new", label: "New Hospitalizations" },
                    { value: "re", label: "Re-Hospitalizations" },
                  ],
                  selected: (v) => (Array.isArray(hospitalizationFilter) ? hospitalizationFilter : []).includes(v),
                  toggle: (v) => {
                    const arr = Array.isArray(hospitalizationFilter) ? hospitalizationFilter : [];
                    setHospitalizationFilter(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
                  },
                },
                {
                  key: "dcer",
                  label: "DC / ER",
                  options: [{ value: "DC", label: "DC" }, { value: "ER", label: "ER" }],
                  selected: (v) => (Array.isArray(dcerFilter) ? dcerFilter : []).includes(v),
                  toggle: (v) => {
                    const arr = Array.isArray(dcerFilter) ? dcerFilter : [];
                    setDcerFilter(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
                  },
                },
                {
                  key: "returns",
                  label: "Returns",
                  options: [
                    { value: "returned", label: "Returned" },
                    { value: "didntReturn", label: "Didn't Return" },
                  ],
                  selected: (v) => (Array.isArray(returnsFilter) ? returnsFilter : []).includes(v),
                  toggle: (v) => {
                    const arr = Array.isArray(returnsFilter) ? returnsFilter : [];
                    setReturnsFilter(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
                  },
                },
                {
                  key: "ninetyDays",
                  label: HOSPITAL_CARDS_LABELS.ninetyDaysData || "90 Days",
                  options: ninetyDaysOptions.map((b) => ({ value: b._id, label: b.label })),
                  selected: (v) => ninetyDaysFilter.map((x) => String(x)).indexOf(String(v)) > -1,
                  toggle: (v) => {
                    const selected = ninetyDaysFilter.map((x) => String(x)).indexOf(String(v)) > -1;
                    setNinetyDaysFilter(selected ? ninetyDaysFilter.filter((x) => String(x) !== String(v)) : [...ninetyDaysFilter, v]);
                  },
                },
                {
                  key: "dayOfWeek",
                  label: "Transfers per day of the week",
                  options: availableDays.map((d) => ({ value: d, label: d })),
                  selected: (v) => daysFilter.includes(v),
                  toggle: (v) => setDaysFilter(daysFilter.includes(v) ? daysFilter.filter((d) => d !== v) : [...daysFilter, v]),
                },
                {
                  key: "shift",
                  label: "Transfers per shift",
                  options: availableShifts.map((s) => ({ value: s, label: s })),
                  selected: (v) => shiftFilter.includes(v),
                  toggle: (v) => setShiftFilter(shiftFilter.includes(v) ? shiftFilter.filter((s) => s !== v) : [...shiftFilter, v]),
                }
              );
            }

            if (filters.type === "communityTransfer") {
              allSections.push(
                {
                  key: "communityTransferType",
                  label: "Community Transfer Type",
                  options: [
                    { value: "safeDischarge", label: COMMUNITY_CARD_LABELS.safeDischarge },
                    { value: "SNF", label: COMMUNITY_CARD_LABELS.SNF },
                    { value: "AMA", label: COMMUNITY_CARD_LABELS.AMA },
                  ],
                  selected: (v) => (Array.isArray(filters.transferType) ? filters.transferType : []).includes(v),
                  toggle: (v) => {
                    const arr = Array.isArray(filters.transferType) ? filters.transferType : [];
                    setFilters((f) => ({ ...f, transferType: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] }));
                  },
                },
                {
                  key: "returns",
                  label: COMMUNITY_CARD_LABELS.returnsData || "Returned / Didn't Return",
                  options: [
                    { value: "returned", label: "Returned" },
                    { value: "didntReturn", label: "Didn't Return" },
                  ],
                  selected: (v) => (Array.isArray(returnsFilter) ? returnsFilter : []).includes(v),
                  toggle: (v) => {
                    const arr = Array.isArray(returnsFilter) ? returnsFilter : [];
                    setReturnsFilter(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
                  },
                },
                {
                  key: "ninetyDays",
                  label: COMMUNITY_CARD_LABELS.sixtyDaysData || "90 Days Analysis",
                  options: ninetyDaysOptions.map((b) => ({ value: b._id, label: b.label })),
                  selected: (v) => ninetyDaysFilter.map((x) => String(x)).indexOf(String(v)) > -1,
                  toggle: (v) => {
                    const selected = ninetyDaysFilter.map((x) => String(x)).indexOf(String(v)) > -1;
                    setNinetyDaysFilter(selected ? ninetyDaysFilter.filter((x) => String(x) !== String(v)) : [...ninetyDaysFilter, v]);
                  },
                },
                {
                  key: "dayOfWeek",
                  label: "Transfers per day of the week",
                  options: availableDays.map((d) => ({ value: d, label: d })),
                  selected: (v) => daysFilter.includes(v),
                  toggle: (v) => setDaysFilter(daysFilter.includes(v) ? daysFilter.filter((d) => d !== v) : [...daysFilter, v]),
                }
              );
            }

            if (filters.type === "deceased") {
              allSections.push(
                {
                  key: "ninetyDays",
                  label: HOSPITAL_CARDS_LABELS.ninetyDaysData || "90 Days Analysis",
                  options: ninetyDaysOptions.map((b) => ({ value: b._id, label: b.label })),
                  selected: (v) => ninetyDaysFilter.map((x) => String(x)).indexOf(String(v)) > -1,
                  toggle: (v) => {
                    const selected = ninetyDaysFilter.map((x) => String(x)).indexOf(String(v)) > -1;
                    setNinetyDaysFilter(selected ? ninetyDaysFilter.filter((x) => String(x) !== String(v)) : [...ninetyDaysFilter, v]);
                  },
                },
                {
                  key: "dayOfWeek",
                  label: "Transfers per day of the week",
                  options: availableDays.map((d) => ({ value: d, label: d })),
                  selected: (v) => daysFilter.includes(v),
                  toggle: (v) => setDaysFilter(daysFilter.includes(v) ? daysFilter.filter((d) => d !== v) : [...daysFilter, v]),
                }
              );
            }

            if (filters.type === "admission") {
              allSections.push(
                {
                  key: "admissionType",
                  label: "Type",
                  options: [
                    { value: "admission", label: "Admission" },
                    { value: "reAdmission", label: "Re-Admission" },
                  ],
                  selected: (v) => (Array.isArray(filters.admissionType) ? filters.admissionType : []).includes(v),
                  toggle: (v) => {
                    const arr = Array.isArray(filters.admissionType) ? filters.admissionType : [];
                    setFilters((f) => ({ ...f, admissionType: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] }));
                  },
                },
                {
                  key: "dayOfWeek",
                  label: "Transfers per day of the week",
                  options: availableDays.map((d) => ({ value: d, label: d })),
                  selected: (v) => daysFilter.includes(v),
                  toggle: (v) => setDaysFilter(daysFilter.includes(v) ? daysFilter.filter((d) => d !== v) : [...daysFilter, v]),
                }
              );
            }

            if (currentDashboardPage && Object.keys(customTabFilterStats).length > 0) {
              Object.entries(customTabFilterStats).forEach(([accessor, { label, items }]) => {
                const sectionKey = `customTab_${accessor}`;
                const selectedIds = customTabFilterValues[accessor] || [];
                const options = (items || []).map((opt) => ({ value: opt.value, label: opt.label }));
                if (!options.length) return;
                allSections.push({
                  key: sectionKey,
                  label,
                  options,
                  selected: (v) => selectedIds.includes(v),
                  toggle: (v) => {
                    setCustomTabFilterValues((prev) => {
                      const prevVals = prev[accessor] || [];
                      const exists = prevVals.includes(v);
                      const nextVals = exists ? prevVals.filter((x) => x !== v) : [...prevVals, v];
                      return { ...prev, [accessor]: nextVals };
                    });
                  },
                });
              });
            }

            const query = advancedFiltersQuery.trim().toLowerCase();
            const visibleSections = allSections
              .map((section) => {
                const sortedOptions = [...section.options].sort((a, b) => a.label.localeCompare(b.label));
                const visibleOptions = query
                  ? sortedOptions.filter(
                    (opt) =>
                      opt.label.toLowerCase().includes(query) ||
                      section.label.toLowerCase().includes(query)
                  )
                  : sortedOptions;
                if (visibleOptions.length === 0 && !section.label.toLowerCase().includes(query)) return null;
                return { ...section, options: visibleOptions };
              })
              .filter(Boolean)
              .sort((a, b) => a.label.localeCompare(b.label));

            return visibleSections.map((section) => {
              const activeInSection = section.options.filter((opt) => section.selected(opt.value)).length;
              return (
                <Box key={section.key} sx={{ borderBottom: "1px solid #f3f4f6" }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setExpandedFilterSections((s) => ({ ...s, [section.key]: !s[section.key] }))}
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 2.5,
                      py: 1.5,
                      border: "none",
                      bgcolor: "transparent",
                      color: "#111827",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      textAlign: "left",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": { bgcolor: "#f9fafb" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontSize: "inherit", fontWeight: "inherit", color: activeInSection > 0 ? PRIMARY : "inherit" }}>
                        {section.label}
                      </Typography>
                      {activeInSection > 0 && (
                        <Box sx={{ minWidth: 18, height: 18, borderRadius: "10px", bgcolor: PRIMARY, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.65rem", fontWeight: 700 }}>
                          {activeInSection}
                        </Box>
                      )}
                    </Box>
                    {expandedFilterSections[section.key] ? <ExpandLessIcon sx={{ fontSize: 20, color: "#9ca3af" }} /> : <ExpandMoreIcon sx={{ fontSize: 20, color: "#9ca3af" }} />}
                  </Box>
                  <Collapse in={expandedFilterSections[section.key]}>
                    <Box sx={{ px: 2, pb: 2 }}>
                      {section.options.map((opt) => {
                        const isSelected = section.selected(opt.value);
                        return (
                          <Box
                            key={opt.value}
                            onClick={() => section.toggle(opt.value)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              px: 1,
                              py: 0.5,
                              mb: 0.25,
                              cursor: "pointer",
                              borderRadius: "8px",
                              transition: "all 0.15s ease",
                              bgcolor: isSelected ? "rgba(37, 99, 235, 0.04)" : "transparent",
                              "&:hover": { bgcolor: isSelected ? "rgba(37, 99, 235, 0.08)" : "#f3f4f6" },
                            }}
                          >
                            <Checkbox size="small" checked={isSelected} sx={{ p: 1, color: "#d1d5db", "&.Mui-checked": { color: PRIMARY } }} />
                            <Typography sx={{ fontSize: "0.8125rem", color: isSelected ? "#111827" : "#4b5563", fontWeight: isSelected ? 600 : 400 }}>
                              {opt.label}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Collapse>
                </Box>
              );
            });
          })()}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderTop: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1.5,
            bgcolor: "#f9fafb",
          }}
        >
          <Button
            size="small"
            onClick={() => setAdvancedFiltersAnchorEl(null)}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontSize: '0.8rem',
              fontWeight: 600,
              px: 2,
              borderColor: '#d1d5db',
              color: '#4b5563',
              '&:hover': { borderColor: '#9ca3af', bgcolor: '#f3f4f6' }
            }}
          >
            Close
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <div className={`page-content ffmar ${styles.residenceListPage}`}>
      {/* 
        Single Unified Box containing the filters and table.
      */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", bgcolor: "#fff" }}>

        {isMobile ? (
          /* Mobile Layout Wrapper */
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            
            {/* 1. Toggle bar (persistent) */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 1.5,
                py: 0.75,
                borderBottom: "1px solid #E8E8EB",
                bgcolor: "#f9fafb",
                gap: 1
              }}
            >
              {/* Left side: Selected Type & active filters count */}
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: "#4b5563",
                  fontSize: "0.72rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.25,
                  flexShrink: 0
                }}
              >
                <FilterAltIcon sx={{ fontSize: 14, color: appliedAdvancedFilters.length > 0 ? PRIMARY : "#9ca3af" }} />
                Type: <span style={{ color: "#111827", fontWeight: 800 }}>{adtTypes.find(o => o.value === filters.type)?.label || filters.type}</span>
                {appliedAdvancedFilters.length > 0 && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setAdvancedFiltersSummaryAnchorEl(e.currentTarget);
                    }}
                    style={{
                      color: PRIMARY,
                      cursor: "pointer",
                      textDecoration: "underline",
                      marginLeft: "2px"
                    }}
                  >
                    {`(+${appliedAdvancedFilters.length})`}
                  </span>
                )}
              </Typography>

              {/* Middle: Integrated Search Bar */}
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <TextField
                  placeholder="Search..."
                  value={filters.search}
                  onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ mr: 0.25 }}>
                        <Tooltip title="Search Column" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSearchColumnAnchorEl(e.currentTarget);
                            }}
                            sx={{ color: "#9ca3af", p: 0, "&:hover": { color: PRIMARY, bgcolor: "transparent" } }}
                          >
                            <SearchIcon sx={{ color: (selectedSearchColumnIds.length > 0 && selectedSearchColumnIds.length < searchableColumns.length) ? PRIMARY : "#9ca3af", width: 16, height: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end" sx={{ gap: 0.25 }}>
                        {(filters.search || "").trim() && (
                          <Box
                            onClick={() => handleGlobalSearchNavigation("next")}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "#f3f4f6",
                              borderRadius: "10px",
                              px: 0.5,
                              py: 0.2,
                              border: "1px solid #e5e7eb",
                              cursor: "pointer",
                              "&:hover": { bgcolor: "#e5e7eb" }
                            }}
                          >
                            <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700, fontSize: "0.55rem" }}>
                              {allGlobalMatches.length > 0 ? globalSearchMatchIndex + 1 : 0}/{allGlobalMatches.length}
                            </Typography>
                          </Box>
                        )}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSearchColumnsAnchorEl(e.currentTarget);
                          }}
                          sx={{ color: (selectedSearchColumnIds.length > 0 && selectedSearchColumnIds.length < searchableColumns.length) ? PRIMARY : "#9ca3af", p: 0.25 }}
                        >
                          <ViewColumnIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: "20px",
                      bgcolor: "#fff",
                      border: "1px solid #E8E8EB",
                      "& fieldset": { border: "none" },
                      height: 28,
                      "& .MuiInputBase-input": { py: 0, height: 28, boxSizing: "border-box", fontSize: "0.72rem" },
                    }
                  }}
                  sx={{ width: "100%", "& .MuiOutlinedInput-root": { height: 28 } }}
                />
              </Box>

              {/* Right side: Show/Hide Filters and optionally hidden columns indicators */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                {(hiddenExtraColumns.length > 0 || hiddenBaseColumns.length > 0) && (
                  <Tooltip title="Show Hidden Columns" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => setHiddenColumnsAnchorEl(e.currentTarget)}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        color: PRIMARY,
                        bgcolor: "#f9fafb",
                        border: "1px solid #e5e7eb",
                        "&:hover": { bgcolor: "#eff6ff" },
                      }}
                    >
                      <VisibilityIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Button
                  size="small"
                  onClick={() => setIsMobileFiltersCollapsed(!isMobileFiltersCollapsed)}
                  startIcon={isMobileFiltersCollapsed ? <ExpandMoreIcon sx={{ fontSize: 14 }} /> : <ExpandLessIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    color: PRIMARY,
                    py: 0.25,
                    px: 1,
                    height: 28,
                    borderRadius: "20px",
                    border: "1px solid",
                    borderColor: "rgba(37, 99, 235, 0.2)",
                    bgcolor: "rgba(37, 99, 235, 0.05)",
                    "&:hover": { bgcolor: "rgba(37, 99, 235, 0.1)" }
                  }}
                >
                  {isMobileFiltersCollapsed ? "Filters" : "Hide"}
                </Button>
              </Box>
            </Box>

            {/* 2. Filters Box (collapsible) */}
            {!isMobileFiltersCollapsed && (
              <Box
                sx={{
                  px: 2,
                  py: 1.25,
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 1,
                  borderBottom: "1px solid #E8E8EB",
                  bgcolor: "#fff",
                }}
              >
                {/* 2.1 Transfer Type Select */}
                <FormControl size="small" sx={{ flex: "0 0 155px", minWidth: 145, height: MOBILE_HEADER_CONTROL_HEIGHT }}>
                  <Select
                    value={filters.type}
                    onChange={(e) => {
                      const newType = e.target.value;
                      handleResetAdvancedFilters({ type: newType });
                    }}
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                      bgcolor: "#f3f4f6",
                      color: "#111827",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      borderRadius: "20px",
                      height: MOBILE_HEADER_CONTROL_HEIGHT,
                      "& .MuiSelect-select": { py: 0, height: MOBILE_HEADER_CONTROL_HEIGHT, display: "flex", alignItems: "center" },
                      "&:hover": { bgcolor: "#e5e7eb" },
                    }}
                  >
                    {adtTypes.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value} sx={{ typography: "body2", borderRadius: 1 }}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* 2.2 Advanced Filters Button */}
                {filters.type !== "all" && (
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={(e) => setAdvancedFiltersAnchorEl(advancedFiltersAnchorEl ? null : e.currentTarget)}
                    startIcon={
                      <FilterAltIcon
                        fontSize={isMobile ? "small" : undefined}
                        sx={{
                          color: appliedAdvancedFilters.length > 0 || advancedFiltersAnchorEl ? PRIMARY : "#9ca3af",
                        }}
                      />
                    }
                    sx={{
                      borderRadius: "20px",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: appliedAdvancedFilters.length > 0 || advancedFiltersAnchorEl ? PRIMARY : "#4b5563",
                      borderColor: appliedAdvancedFilters.length > 0 || advancedFiltersAnchorEl ? PRIMARY : "#E8E8EB",
                      backgroundColor: advancedFiltersAnchorEl ? "#f0f4ff" : "transparent",
                      height: MOBILE_HEADER_CONTROL_HEIGHT,
                      px: 1.25,
                      "&:hover": {
                        borderColor: PRIMARY,
                        bgcolor: "#f9fafb",
                      },
                    }}
                  >
                    Filters
                    {appliedAdvancedFilters.length > 0 && (
                      <Box
                        component="span"
                        sx={{
                          ml: 0.5,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.25,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Box
                          component="span"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAdvancedFiltersSummaryAnchorEl(e.currentTarget);
                          }}
                          sx={{
                            px: 0.5,
                            py: 0.1,
                            borderRadius: "999px",
                            bgcolor: "#e5e7eb",
                            color: PRIMARY,
                            fontSize: "0.65rem",
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                        >
                          {appliedAdvancedFilters.length}
                        </Box>
                      </Box>
                    )}
                  </Button>
                )}

                {/* 2.3 Date Range Picker */}
                <Box sx={{ flex: isMobile ? "1 1 auto" : "0 0 180px", minWidth: isMobile ? 220 : "auto", ml: "auto" }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.25}
                    sx={{
                      width: "100%",
                      border: "1px solid #E8E8EB",
                      borderRadius: "20px",
                      bgcolor: "#fff",
                      px: 0.5,
                      height: MOBILE_HEADER_CONTROL_HEIGHT,
                    }}
                  >
                    <TextField
                      type="date"
                      value={filters.fromDate}
                      onChange={(e) => setFilters((f) => ({ ...f, fromDate: e.target.value }))}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ pl: 0.75, pointerEvents: "none", mr: 0 }}>
                            <CalendarTodayIcon sx={{ fontSize: 13, color: "#9ca3af" }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        flex: 1,
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "transparent",
                          color: "#374151",
                          fontWeight: 600,
                          fontSize: "0.65rem",
                          height: MOBILE_HEADER_CONTROL_HEIGHT,
                          padding: 0,
                          "& fieldset": { border: "none" },
                        },
                        "& .MuiOutlinedInput-input": {
                          padding: "0 4px",
                          height: MOBILE_HEADER_CONTROL_HEIGHT,
                          boxSizing: "border-box",
                          textAlign: "center",
                          "&::-webkit-date-and-time-value": {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            margin: 0,
                            padding: 0,
                          }
                        },
                      }}
                    />
                    <span style={{ color: "#d1d5db", fontSize: 10, flexShrink: 0 }}>-</span>
                    <TextField
                      type="date"
                      value={filters.toDate}
                      onChange={(e) => setFilters((f) => ({ ...f, toDate: e.target.value }))}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ pl: 0.75, pointerEvents: "none", mr: 0 }}>
                            <CalendarTodayIcon sx={{ fontSize: 13, color: "#9ca3af" }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        flex: 1,
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "transparent",
                          color: "#374151",
                          fontWeight: 600,
                          fontSize: "0.65rem",
                          height: MOBILE_HEADER_CONTROL_HEIGHT,
                          padding: 0,
                          "& fieldset": { border: "none" },
                        },
                        "& .MuiOutlinedInput-input": {
                          padding: "0 4px",
                          height: MOBILE_HEADER_CONTROL_HEIGHT,
                          boxSizing: "border-box",
                          textAlign: "center",
                          "&::-webkit-date-and-time-value": {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            margin: 0,
                            padding: 0,
                          }
                        },
                      }}
                    />
                  </Stack>
                </Box>

                {/* 2.4 Actions (Download / Share) */}
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    height: MOBILE_HEADER_CONTROL_HEIGHT,
                    border: "1px solid #E8E8EB",
                    borderRadius: "20px",
                    bgcolor: "#fff",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                    px: 0.5,
                  }}
                >
                  <Tooltip title="Download" arrow>
                    <IconButton
                      size="small"
                      onClick={handleDownloadExcel}
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        color: "#6b7280",
                        bgcolor: "#f9fafb",
                        border: "1px solid #e5e7eb",
                        "&:hover": { color: PRIMARY, bgcolor: "#eff6ff" },
                      }}
                    >
                      <DownloadIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Share Report" arrow>
                    <IconButton
                      size="small"
                      onClick={() => handleOnClickShareReport("Residents List", { useExcel: true })}
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        color: "#6b7280",
                        bgcolor: "#f9fafb",
                        border: "1px solid #e5e7eb",
                        "&:hover": { color: PRIMARY, bgcolor: "#eff6ff" },
                      }}
                    >
                      <ShareIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          /* Original Desktop Layout */
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-end",
              gap: HEADER_GAP,
              borderBottom: "1px solid #E8E8EB",
            }}
          >
            <Stack direction="column" alignItems="flex-start" spacing={0.5} sx={{ flexShrink: 0, pb: 0 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "#6b7280", letterSpacing: "0.5px", fontSize: "0.75rem", lineHeight: 1.2, px: 0 }}>Transfers Type:</Typography>
              <FormControl size="small" sx={{ minWidth: { xs: 180, sm: 260 }, height: HEADER_CONTROL_HEIGHT }}>
                <Select
                  value={filters.type}
                  onChange={(e) => {
                    const newType = e.target.value;
                    handleResetAdvancedFilters({ type: newType });
                  }}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    bgcolor: "#f3f4f6",
                    color: "#111827",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    borderRadius: "20px",
                    height: HEADER_CONTROL_HEIGHT,
                    minHeight: HEADER_CONTROL_HEIGHT,
                    px: 1.5,
                    "& .MuiSelect-select": { py: 0, height: HEADER_CONTROL_HEIGHT, minHeight: HEADER_CONTROL_HEIGHT, boxSizing: "border-box", display: "flex", alignItems: "center" },
                    "&:hover": { bgcolor: "#e5e7eb" },
                  }}
                >
                  {adtTypes.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value} sx={{ typography: "body2", borderRadius: 1 }}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: { xs: "1 1 100%", sm: "0 0 auto" }, minWidth: 0 }}>
              <TextField
                placeholder="Search by First Name, Last Name..."
                value={filters.search}
                onChange={(e) => {
                  setFilters((f) => ({ ...f, search: e.target.value }));
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Tooltip title="Search column" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSearchColumnAnchorEl(e.currentTarget);
                          }}
                          sx={{
                            color: "#9ca3af",
                            p: 0,
                            "&:hover": { color: PRIMARY, bgcolor: "transparent" },
                          }}
                        >
                          <SearchIcon sx={{ color: (selectedSearchColumnIds.length > 0 && selectedSearchColumnIds.length < searchableColumns.length) ? PRIMARY : "#9ca3af", width: 22, height: 22 }} />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end" sx={{ gap: 0.5, mr: 0.5 }}>
                      {(filters.search || "").trim() && (
                        <Tooltip title="Find next match" arrow>
                          <Box
                            onClick={() => handleGlobalSearchNavigation("next")}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              bgcolor: "#f3f4f6",
                              borderRadius: "12px",
                              px: 1.2,
                              py: 0.4,
                              border: "1px solid #e5e7eb",
                              cursor: "pointer",
                              transition: "all 0.2s",
                              "&:hover": { bgcolor: "#e5e7eb" }
                            }}
                          >
                            <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700, fontSize: "0.7rem" }}>
                              Find: {allGlobalMatches.length > 0 ? globalSearchMatchIndex + 1 : 0} / {allGlobalMatches.length}
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}
                      <Tooltip title="Search in columns" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSearchColumnsAnchorEl(e.currentTarget);
                          }}
                          sx={{
                            color: (selectedSearchColumnIds.length > 0 && selectedSearchColumnIds.length < searchableColumns.length) ? PRIMARY : "#9ca3af",
                            p: 0.5,
                            "&:hover": { color: PRIMARY, bgcolor: "transparent" },
                          }}
                        >
                          <ViewColumnIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: "20px",
                    bgcolor: "#fff",
                    width: { xs: "100%", sm: 260, md: 320 },
                    border: "1px solid #E8E8EB",
                    "& fieldset": { border: "none" },
                    height: HEADER_CONTROL_HEIGHT,
                    minHeight: HEADER_CONTROL_HEIGHT,
                    "& .MuiInputBase-input": { py: 0, height: HEADER_CONTROL_HEIGHT, minHeight: HEADER_CONTROL_HEIGHT, boxSizing: "border-box" },
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": { height: HEADER_CONTROL_HEIGHT, minHeight: HEADER_CONTROL_HEIGHT },
                }}
              />
              {(hiddenExtraColumns.length > 0 || hiddenBaseColumns.length > 0) && (
                <Tooltip title="Show hidden columns" arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      setHiddenColumnsAnchorEl(e.currentTarget);
                    }}
                    sx={{
                      ml: 0.5,
                      width: HEADER_CONTROL_HEIGHT,
                      height: HEADER_CONTROL_HEIGHT,
                      minWidth: HEADER_CONTROL_HEIGHT,
                      minHeight: HEADER_CONTROL_HEIGHT,
                      color: PRIMARY,
                      bgcolor: "#f9fafb",
                      border: "1px solid #E8E8EB",
                      borderRadius: "20px",
                      "&:hover": { bgcolor: "#f3f4f6" },
                    }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
              {filters.type !== "all" && (
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={(e) => setAdvancedFiltersAnchorEl(advancedFiltersAnchorEl ? null : e.currentTarget)}
                  startIcon={
                    <FilterAltIcon
                      sx={{
                        color: appliedAdvancedFilters.length > 0 || advancedFiltersAnchorEl ? PRIMARY : "#9ca3af",
                      }}
                    />
                  }
                  sx={{
                    borderRadius: "5px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    color: appliedAdvancedFilters.length > 0 || advancedFiltersAnchorEl ? PRIMARY : "#4b5563",
                    borderColor: appliedAdvancedFilters.length > 0 || advancedFiltersAnchorEl ? PRIMARY : "#E8E8EB",
                    backgroundColor: advancedFiltersAnchorEl ? "#f0f4ff" : "transparent",
                    height: HEADER_CONTROL_HEIGHT,
                    minHeight: HEADER_CONTROL_HEIGHT,
                    px: 2,
                    "&:hover": {
                      borderColor: PRIMARY,
                      bgcolor: "#f9fafb",
                    },
                  }}
                >
                  Filters
                  {appliedAdvancedFilters.length > 0 && (
                    <Box
                      component="span"
                      sx={{
                        ml: 0.75,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.25,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Box
                        component="span"
                        onClick={(e) => {
                          e.stopPropagation();
                          setAdvancedFiltersSummaryAnchorEl(e.currentTarget);
                        }}
                        sx={{
                          px: 0.75,
                          py: 0.1,
                          borderRadius: "999px",
                          bgcolor: "#e5e7eb",
                          color: PRIMARY,
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        {appliedAdvancedFilters.length}
                      </Box>
                      <Tooltip title="Clear advanced filters" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleResetAdvancedFilters();
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          sx={{
                            p: 0.25,
                            color: PRIMARY,
                            "&:hover": { bgcolor: "rgba(37, 99, 235, 0.08)", color: PRIMARY },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Button>
              )}
            </Stack>

            <Stack direction="row" spacing={HEADER_GAP} alignItems="flex-end" sx={{ ml: "auto", flexShrink: 0 }}>
              <Stack direction="column" alignItems="flex-start" spacing={0.5} sx={{ flexShrink: 0, pb: 0 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "#6b7280", letterSpacing: "0.5px", fontSize: "0.75rem", lineHeight: 1.2, px: 0 }}>&nbsp;</Typography>
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.75,
                    height: HEADER_CONTROL_HEIGHT,
                    minHeight: HEADER_CONTROL_HEIGHT,
                    border: "1px solid #E8E8EB",
                    borderRadius: "20px",
                    bgcolor: "#fff",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                    px: 0.75,
                  }}
                >
                  <Tooltip title="Download" arrow>
                    <IconButton
                      size="small"
                      onClick={handleDownloadExcel}
                      sx={{
                        width: 32,
                        height: 32,
                        minWidth: 32,
                        minHeight: 32,
                        borderRadius: "50%",
                        color: "#6b7280",
                        bgcolor: "#f9fafb",
                        border: "1px solid #e5e7eb",
                        "&:hover": {
                          color: PRIMARY,
                          bgcolor: "#eff6ff",
                          borderColor: "rgba(37, 99, 235, 0.3)",
                        },
                      }}
                    >
                      <DownloadIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Share Report" arrow>
                    <IconButton
                      size="small"
                      onClick={() => handleOnClickShareReport("Residents List", { useExcel: true })}
                      sx={{
                        width: 32,
                        height: 32,
                        minWidth: 32,
                        minHeight: 32,
                        borderRadius: "50%",
                        color: "#6b7280",
                        bgcolor: "#f9fafb",
                        border: "1px solid #e5e7eb",
                        "&:hover": {
                          color: PRIMARY,
                          bgcolor: "#eff6ff",
                          borderColor: "rgba(37, 99, 235, 0.3)",
                        },
                      }}
                    >
                      <ShareIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Stack>

              <Stack direction="column" alignItems="flex-start" spacing={0.5} sx={{ flexShrink: 0, pb: 0 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "#6b7280", letterSpacing: "0.5px", fontSize: "0.75rem", lineHeight: 1.2, px: 0.5 }}>Select Dates:</Typography>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    border: "1px solid #E8E8EB",
                    borderRadius: "20px",
                    bgcolor: "#fff",
                    px: 1,
                    height: HEADER_CONTROL_HEIGHT,
                    minHeight: HEADER_CONTROL_HEIGHT,
                  }}
                >
                  <TextField
                    type="date"
                    value={filters.fromDate}
                    onChange={(e) => {
                      setFilters((f) => ({ ...f, fromDate: e.target.value }));
                    }}
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "transparent",
                        color: "#374151",
                        fontWeight: 600,
                        fontSize: "0.8125rem",
                        height: HEADER_CONTROL_HEIGHT,
                        minHeight: HEADER_CONTROL_HEIGHT,
                        padding: 0,
                        "& fieldset": { border: "none" },
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "0 10px",
                        height: HEADER_CONTROL_HEIGHT,
                        minHeight: HEADER_CONTROL_HEIGHT,
                        boxSizing: "border-box",
                      },
                    }}
                  />
                  <ArrowForwardIcon sx={{ color: "#d1d5db", fontSize: 14, flexShrink: 0 }} />
                  <TextField
                    type="date"
                    value={filters.toDate}
                    onChange={(e) => {
                      setFilters((f) => ({ ...f, toDate: e.target.value }));
                    }}
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "transparent",
                        color: "#374151",
                        fontWeight: 600,
                        fontSize: "0.8125rem",
                        height: HEADER_CONTROL_HEIGHT,
                        minHeight: HEADER_CONTROL_HEIGHT,
                        padding: 0,
                        "& fieldset": { border: "none" },
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "0 10px",
                        height: HEADER_CONTROL_HEIGHT,
                        minHeight: HEADER_CONTROL_HEIGHT,
                        boxSizing: "border-box",
                      },
                    }}
                  />
                </Stack>
              </Stack>
            </Stack>
          </Box>
        )}

        {isMobile ? (
          <Drawer
            anchor="bottom"
            open={Boolean(advancedFiltersAnchorEl)}
            onClose={() => setAdvancedFiltersAnchorEl(null)}
            PaperProps={{
              sx: {
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                maxHeight: "85vh",
                display: "flex",
                flexDirection: "column",
              }
            }}
          >
            {renderAdvancedFiltersContent()}
          </Drawer>
        ) : (
          <Popover
            open={Boolean(advancedFiltersAnchorEl)}
            anchorEl={advancedFiltersAnchorEl}
            onClose={() => setAdvancedFiltersAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{
              paper: {
                sx: {
                  mt: 1.5,
                  minWidth: 360,
                  maxWidth: 480,
                  maxHeight: "80vh",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 2,
                  boxShadow: "0 10px 30px rgba(15,23,42,0.25)",
                  overflow: "hidden",
                },
              },
            }}
          >
            {renderAdvancedFiltersContent()}
          </Popover>
        )}


        <Menu
          anchorEl={searchColumnsAnchorEl}
          open={Boolean(searchColumnsAnchorEl)}
          onClose={() => {
            setSearchColumnsAnchorEl(null);
            setColumnMenuQuery("");
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          MenuListProps={{ dense: true, disableAutoFocusItem: true }}
          PaperProps={{
            sx: {
              mt: 0.5,
              minWidth: 200,
              maxWidth: "90vw",
              maxHeight: 400,
              overflowY: "auto",
              "& .MuiMenuItem-root": { minHeight: 32, py: 0.25, px: 1.5 },
              "& .MuiCheckbox-root": { p: 0.25, mr: 1 },
              "& .MuiListItemText-primary": { fontSize: "0.8125rem" },
            },
          }}
        >
          <MenuItem dense disabled sx={{ opacity: 0.9 }}>
            <ListItemText primary="Select columns to include in search" primaryTypographyProps={{ fontSize: "0.75rem", fontWeight: 600 }} />
          </MenuItem>
          <Box sx={{ px: 1.5, py: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search columns..."
              value={columnMenuQuery}
              onChange={(e) => setColumnMenuQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#9ca3af", fontSize: 18 }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: "8px", bgcolor: "#f9fafb", fontSize: "0.8125rem" }
              }}
            />
          </Box>
          <Divider />
          {!columnMenuQuery.trim() && [
            <MenuItem
              key="select-all-columns"
              onClick={() => {
                const allVisibleIds = searchableColumns.map((c) => c.id);
                const isAllVisibleSelected = allVisibleIds.every(id => selectedSearchColumnIds.includes(id));
                if (isAllVisibleSelected) {
                  // Unselect only those that are currently visible in the menu
                  setSelectedSearchColumnIds(prev => prev.filter(id => !allVisibleIds.includes(id)));
                } else {
                  // Union the current selection with all visible IDs
                  setSelectedSearchColumnIds(prev => _.union(prev, allVisibleIds));
                }
              }}
            >
              <Checkbox
                size="small"
                checked={searchableColumns.every(c => selectedSearchColumnIds.includes(c.id))}
                indeterminate={
                  !searchableColumns.every(c => selectedSearchColumnIds.includes(c.id)) &&
                  searchableColumns.some(c => selectedSearchColumnIds.includes(c.id))
                }
              />
              <ListItemText primary="Select All columns" />
            </MenuItem>,
            <Divider key="select-all-divider" />
          ]}
          {searchableBaseColumns.filter(c => c.label.toLowerCase().includes(columnMenuQuery.toLowerCase())).length > 0 &&
            searchableBaseColumns
              .filter(c => c.label.toLowerCase().includes(columnMenuQuery.toLowerCase()))
              .map((col) => (
                <MenuItem
                  key={col.id}
                  onClick={() => {
                    setSelectedSearchColumnIds((prev) =>
                      prev.includes(col.id)
                        ? prev.filter((id) => id !== col.id)
                        : [...prev, col.id]
                    );
                  }}
                >
                  <Checkbox size="small" checked={selectedSearchColumnIds.includes(col.id)} />
                  <ListItemText primary={col.label} />
                </MenuItem>
              ))
          }
          {extraColumns.filter(c => c.label.toLowerCase().includes(columnMenuQuery.toLowerCase())).length > 0 &&
            extraColumns
              .filter(c => c.label.toLowerCase().includes(columnMenuQuery.toLowerCase()))
              .map((col) => (
                <MenuItem
                  key={col.id}
                  onClick={() => {
                    setSelectedSearchColumnIds((prev) =>
                      prev.includes(col.id)
                        ? prev.filter((id) => id !== col.id)
                        : [...prev, col.id]
                    );
                  }}
                >
                  <Checkbox size="small" checked={selectedSearchColumnIds.includes(col.id)} />
                  <ListItemText primary={col.label} />
                </MenuItem>
              ))
          }
        </Menu>

        {advancedFiltersSummaryAnchorEl && (
          <Menu
            anchorEl={advancedFiltersSummaryAnchorEl}
            open={Boolean(advancedFiltersSummaryAnchorEl)}
            onClose={() => setAdvancedFiltersSummaryAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            MenuListProps={{ dense: true }}
            PaperProps={{ sx: { maxWidth: "90vw" } }}
          >
            {appliedAdvancedFilters.length === 0 ? (
              <MenuItem disabled>
                <ListItemText primary="No advanced filters applied" />
              </MenuItem>
            ) : (
              appliedAdvancedFilters.map((item, idx) => (
                <MenuItem key={`${item}-${idx}`}>
                  <ListItemText primary={item} />
                </MenuItem>
              ))
            )}
          </Menu>
        )}

        {/* Table Display */}
        {loading ? (
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0, px: 0, pb: isMobile ? 0 : 2 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
                width: "100%",
                height: isMobile
                  ? (isMobileFiltersCollapsed ? "calc(100vh - 120px)" : "calc(100vh - 169px)")
                  : "calc(100vh - 100px)",
                minHeight: isMobile ? 200 : 360,
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                bgcolor: "#fff",
                gap: 2
              }}
            >
              <CircularProgress size={36} sx={{ color: PRIMARY }} />
              <Typography variant="body2" color="text.secondary">
                Loading residents...
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0, px: 0, pb: isMobile ? 0 : 2 }}>
            <TableContainer
              key={filters.type}
              ref={(node) => {
                tableContainerRef.current = node;
              }}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={handleTouchEnd}
              sx={{
                flex: 1,
                width: "100%",
                maxWidth: "100%",
                overflowX: isMobile && isScrollingVertically ? "hidden" : "auto",
                overflowY: isMobile && isScrollingHorizontally ? "hidden" : "auto",
                WebkitOverflowScrolling: "touch",
                overscrollBehavior: "none",
                height: isMobile
                  ? (isMobileFiltersCollapsed ? "calc(100dvh - 120px)" : "calc(100dvh - 169px)")
                  : "auto",
                maxHeight: isMobile
                  ? (isMobileFiltersCollapsed ? "calc(100dvh - 120px)" : "calc(100dvh - 169px)")
                  : "calc(100vh - 100px)",
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                bgcolor: "#fff"
              }}
            >
              <Table
                stickyHeader
                sx={{
                  display: "table",
                  minWidth: "100%",
                  width: "max-content",
                  borderCollapse: "separate",
                  borderSpacing: "0 0",
                  // 3px vertical column borders, row borders handled on rows
                  "& .MuiTableCell-root": {
                    borderRight: "3px solid #E5E7EB",
                  },
                  "& .MuiTableRow-root > .MuiTableCell-root:last-of-type": {
                    borderRight: "none",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {filters.type !== "all" && (
                      <TableCell
                        sx={{
                          color: "#374151",
                          fontWeight: 700,
                          fontSize: "0.65rem",
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                          borderBottom: "2px solid #e5e7eb",
                          borderRight: "3px solid #E5E7EB",
                          width: isMobile ? 40 : 80,
                          minWidth: isMobile ? 40 : 80,
                          p: 0,
                          textAlign: "center",
                          bgcolor: highlightedColumnIndex === -1 ? "rgba(37, 99, 235, 0.08)" : "#F9FAFB",
                          position: "sticky",
                          top: 0,
                          left: 0,
                          zIndex: 40,
                          transition: "background-color 0.3s ease",
                          boxShadow: "2px 0 5px rgba(0,0,0,0.1)",
                          willChange: "transform",
                        }}
                      >
                        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%", justifyContent: "center" }}>
                          <Box sx={{ display: "flex", alignItems: "center", height: isMobile ? 44 : 32 }}>
                            <Tooltip title="Search Column" arrow>
                              <ButtonBase
                                onClick={(e) => setSearchColumnAnchorEl(e.currentTarget)}
                                sx={{
                                  flex: 1,
                                  height: "100%",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  borderRight: "1px solid #e5e7eb",
                                  color: PRIMARY,
                                  "&:hover": { bgcolor: "#f1f5f9" }
                                }}
                              >
                                {isMobile ? <SearchIcon sx={{ fontSize: 20 }} /> : "Search Columns"}
                              </ButtonBase>
                            </Tooltip>
                          </Box>
                        </Box>
                      </TableCell>
                    )}
                    <DndContext
                      sensors={dndSensors}
                      collisionDetection={closestCenter}
                      onDragStart={({ active }) => setActiveColumnId(active.id)}
                      onDragEnd={({ active, over }) => {
                        setActiveColumnId(null);
                        if (!over || active.id === over.id) return;

                        // Identify fixed columns that cannot be moved or displaced
                        const fixedBaseIds = ["lastName", "firstName"];
                        if (showFacilityColumn) fixedBaseIds.unshift("facility");

                        // If trying to move a fixed column, or move something into a fixed position, abort
                        if (fixedBaseIds.includes(active.id) || fixedBaseIds.includes(over.id)) {
                          return;
                        }

                        // Determine if it's a base or extra column
                        if (visibleBaseColumnsInOrder.includes(active.id)) {
                          setBaseColumnOrder((prev) => {
                            const oldIdx = prev.indexOf(active.id);
                            const newIdx = prev.indexOf(over.id);
                            if (oldIdx === -1 || newIdx === -1) return prev;
                            return arrayMove(prev, oldIdx, newIdx);
                          });
                        } else {
                          setQuestionColumnOrder((prev) => {
                            const oldIdx = prev.indexOf(active.id);
                            const newIdx = prev.indexOf(over.id);
                            if (oldIdx === -1 || newIdx === -1) return prev;
                            return arrayMove(prev, oldIdx, newIdx);
                          });
                        }
                      }}
                    >
                      <SortableContext items={visibleBaseColumnsInOrder} strategy={horizontalListSortingStrategy}>
                        {visibleBaseColumnsInOrder.map((baseId, baseIndex) => {
                          const label = BASE_COLUMN_LABELS[baseId] || baseId;
                          const hasSort = true;
                          const sortKey = baseId === "dob" ? "DOB" : (baseId === "dateOfADT" ? "dateOfADT" : baseId);
                          const hasBaseFilter =
                            (baseId === "facility" && (baseColumnFilterValues.facility || "").trim()) ||
                            (baseId === "firstName" && (baseColumnFilterValues.firstName || "").trim()) ||
                            (baseId === "middleInitial" && (baseColumnFilterValues.middleInitial || "").trim()) ||
                            (baseId === "lastName" && (baseColumnFilterValues.lastName || "").trim()) ||
                            (baseId === "type" && (baseColumnFilterValues.type || "").trim()) ||
                            (baseId === "dob" && (baseColumnFilterValues.dobFrom || baseColumnFilterValues.dobTo)) ||
                            (baseId === "dateOfADT" && (baseColumnFilterValues.dateOfADTFrom || baseColumnFilterValues.dateOfADTTo));

                          // Calculate hidden gap before this base column
                          const baseIdxOrder = baseColumnOrder.indexOf(baseId);
                          const prevVisibleBaseId = baseIndex > 0 ? visibleBaseColumnsInOrder[baseIndex - 1] : null;
                          const prevVisibleIdxOrder = prevVisibleBaseId ? baseColumnOrder.indexOf(prevVisibleBaseId) : -1;
                          const hiddenBaseIdsInGap = baseColumnOrder.slice(prevVisibleIdxOrder + 1, baseIdxOrder).filter(id =>
                            !selectedBaseColumnIds.includes(id) &&
                            (id !== "facility" || showFacilityColumn) &&
                            (id !== "type" || filters.type === "all")
                          );
                          const hiddenBaseLabels = hiddenBaseIdsInGap.map(id => BASE_COLUMN_LABELS[id] || id);


                          const isFixed = filters.type !== "all" && (["lastName", "firstName"].includes(baseId) || (baseId === "facility" && showFacilityColumn));
                          let stickyLeft = undefined;
                          if (isFixed) {
                            stickyLeft = isMobile ? 40 : 80;
                            if (baseId === "lastName" && showFacilityColumn) stickyLeft += (isMobile ? 100 : 140);
                            if (baseId === "firstName") {
                              stickyLeft += (isMobile ? 100 : 140);
                              if (showFacilityColumn) stickyLeft += (isMobile ? 100 : 140);
                            }
                          }

                          return (
                            <DraggableTh
                              id={baseId}
                              key={baseId}
                              data-column-index={baseIndex}
                              disabled={isFixed}
                              sx={{
                                bgcolor: activeColumnId === baseId
                                  ? "#DBEAFE" // Full column highlight while dragging
                                  : (filters.search || "").trim() && selectedSearchColumnIds.includes(baseId) && columnSearchMatches[baseId]?.length > 0
                                    ? "#FEF3C7" // Yellow background for search matches
                                    : (highlightedColumnIndex === baseIndex ? "#DBEAFE" : "#F9FAFB"),
                                color: sortBy === sortKey ? PRIMARY : "#374151",
                                fontWeight: sortBy === sortKey ? 700 : 600,
                                fontSize: isMobile ? "0.6875rem" : "0.75rem",
                                letterSpacing: "0.5px",
                                textTransform: "uppercase",
                                borderBottom: "2px solid #e5e7eb",
                                borderRight: isFixed ? "3px solid #E5E7EB" : "1px solid #f3f4f6",
                                py: isMobile ? 1 : 2,
                                whiteSpace: "nowrap",
                                px: baseId === "middleInitial" ? 1 : (isMobile ? 1 : 2),
                                width: baseId === "middleInitial" ? 50 : (["facility", "lastName", "firstName"].includes(baseId) ? (isMobile ? 100 : 140) : "auto"),
                                minWidth: baseId === "middleInitial" ? 50 : (["facility", "lastName", "firstName"].includes(baseId) ? (isMobile ? 100 : 140) : (isMobile ? 90 : 120)),
                                transition: "all 0.2s ease",
                                position: isFixed ? "sticky" : "relative",
                                left: stickyLeft,
                                zIndex: isFixed ? 40 : 10,
                                boxShadow: isFixed ? "2px 0 5px rgba(0,0,0,0.1)" : "none",
                                willChange: isFixed ? "transform" : "auto",
                                "&:hover": {
                                  bgcolor: activeColumnId === baseId
                                    ? "#DBEAFE"
                                    : (filters.search || "").trim() && selectedSearchColumnIds.includes(baseId) && columnSearchMatches[baseId]?.length > 0
                                      ? "#FDE68A" : "#f1f5f9", // Darker yellow on hover
                                  cursor: isFixed ? "default" : "grab",
                                }
                              }}
                            >
                              {!isMobile && hiddenBaseLabels.length > 0 && (
                                <Tooltip
                                  title={
                                    <Box sx={{ p: 0.5 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5, color: "#111827" }}>Hidden Columns:</Typography>
                                      {hiddenBaseLabels.map(lbl => <Typography key={lbl} variant="caption" sx={{ display: "block", color: "#374151" }}>• {lbl}</Typography>)}
                                      <Typography variant="caption" sx={{ display: "block", mt: 0.5, fontStyle: "italic", opacity: 0.8, color: "#6b7280" }}>Double-click to unhide</Typography>
                                    </Box>
                                  }
                                  arrow
                                  placement="top"
                                  slotProps={{
                                    tooltip: {
                                      sx: {
                                        bgcolor: "#fff",
                                        color: "#111827",
                                        border: "1px solid #e5e7eb",
                                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                        "& .MuiTooltip-arrow": {
                                          color: "#fff",
                                          "&::before": {
                                            border: "1px solid #e5e7eb",
                                          }
                                        }
                                      }
                                    }
                                  }}
                                >
                                  <Box
                                    onDoubleClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedBaseColumnIds(prev => [...prev, ...hiddenBaseIdsInGap]);
                                    }}
                                    sx={{
                                      position: "absolute",
                                      left: -2,
                                      top: 0,
                                      bottom: 0,
                                      width: 6,
                                      bgcolor: "#111827",
                                      cursor: "pointer",
                                      zIndex: 2,
                                      "&:hover": { bgcolor: PRIMARY, width: 8, left: -3 }
                                    }}
                                  />
                                </Tooltip>
                              )}
                              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.1, width: "100%" }}>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: "100%", minHeight: 44, justifyContent: "space-between" }}>
                                  <Box
                                    onMouseEnter={(e) => handleHeaderMouseEnter(e, baseId, label)}
                                    onMouseLeave={() => setActiveHeaderTooltip(null)}
                                    onTouchStart={(e) => handleHeaderMouseEnter(e, baseId, label)}
                                    sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, width: "100%", justifyContent: baseId === "middleInitial" ? "center" : "flex-start" }}
                                  >
                                    <Tooltip
                                      title={activeHeaderTooltip?.id === baseId ? (baseId === "middleInitial" ? "Middle Initial" : activeHeaderTooltip.title) : ""}
                                      arrow
                                      placement="top"
                                      slotProps={{
                                        tooltip: {
                                          sx: {
                                            bgcolor: "#fff",
                                            color: "#111827",
                                            border: "1px solid #e5e7eb",
                                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                            "& .MuiTooltip-arrow": {
                                              color: "#fff",
                                              "&::before": {
                                                border: "1px solid #e5e7eb",
                                              }
                                            }
                                          }
                                        }
                                      }}
                                    >
                                      {hasSort ? (
                                        <TableSortLabel
                                          active={sortBy === sortKey}
                                          direction={sortBy === sortKey ? sortDirection : "asc"}
                                          onClick={() => handleSort(sortKey)}
                                          sx={{
                                            color: "inherit !important",
                                            "& .MuiTableSortLabel-icon": {
                                              color: "#1e3a8a !important",
                                              opacity: sortBy === sortKey ? 1 : 0.3,
                                              fontSize: sortBy === sortKey ? "22px !important" : "inherit !important",
                                            }
                                          }}
                                        >
                                          <Box
                                            component="span"
                                            className="truncated-text"
                                            sx={{
                                              display: "block",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                              fontWeight: sortBy === sortKey ? 700 : 600
                                            }}
                                          >
                                            {label}
                                          </Box>
                                        </TableSortLabel>
                                      ) : (
                                        <Box
                                          component="span"
                                          className="truncated-text"
                                          sx={{
                                            display: "block",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap"
                                          }}
                                        >
                                          {label}
                                        </Box>
                                      )}
                                    </Tooltip>
                                  </Box>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, alignSelf: "flex-end", width: "100%", justifyContent: baseId === "middleInitial" ? "center" : "flex-end" }}>

                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                      {/* Match count - clickable */}
                                      {(filters.search || "").trim() && selectedSearchColumnIds.includes(baseId) && columnSearchMatches[baseId]?.length > 0 && (
                                        <Tooltip title="Find next match" arrow>
                                          <Box
                                            onClick={(e) => { e.stopPropagation(); handleScrollToMatch(baseId, "next"); }}
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              bgcolor: "rgba(255,255,255,0.9)",
                                              borderRadius: "12px",
                                              px: isMobile ? 0.8 : 1.2,
                                              py: isMobile ? 0.15 : 0.3,
                                              border: "1px solid #bae6fd",
                                              flexShrink: 0,
                                              cursor: "pointer",
                                              transition: "all 0.2s",
                                              "&:hover": { bgcolor: "#e0f2fe" }
                                            }}
                                          >
                                            <Typography sx={{ fontSize: isMobile ? "0.55rem" : "0.65rem", fontWeight: 800, color: "#0369a1", whiteSpace: "nowrap" }}>
                                              Find: {(currentSearchMatchIndices[baseId] ?? -1) + 1} / {columnSearchMatches[baseId].length}
                                            </Typography>
                                          </Box>
                                        </Tooltip>
                                      )}
                                      <Tooltip title="Filter column" arrow>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setBaseColumnFilterAnchor({ id: baseId, anchorEl: e.currentTarget });
                                          }}
                                          sx={{
                                            p: isMobile ? 0.25 : 0.5,
                                            color: hasBaseFilter ? PRIMARY : "#9ca3af",
                                            "&:hover": {
                                              color: PRIMARY,
                                              bgcolor: "#e5e7eb"
                                            }
                                          }}
                                        >
                                          <FilterAltIcon sx={{ fontSize: isMobile ? 14 : 16 }} />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Hide column" arrow>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedBaseColumnIds((prev) => prev.filter((id) => id !== baseId));
                                          }}
                                          sx={{
                                            p: isMobile ? 0.25 : 0.5,
                                            color: "#9ca3af",
                                            "&:hover": {
                                              color: "#ef4444",
                                              bgcolor: "#fee2e2"
                                            }
                                          }}
                                        >
                                          <VisibilityOffIcon sx={{ fontSize: isMobile ? 14 : 16 }} />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </Box>
                                </Box>
                              </Box>
                            </DraggableTh>
                          );
                        })}
                      </SortableContext>
                      <SortableContext items={visibleExtraColumns.map(c => c.id)} strategy={horizontalListSortingStrategy}>
                        {visibleExtraColumns.map((col, extraIndex) => {
                          const isDateCol = col.isDate || col.label.toLowerCase().includes("date");
                          const isTimeCol = col.isTime || col.label.toLowerCase().includes("time");
                          const width = isDateCol ? (isMobile ? 95 : 130) : (isTimeCol ? (isMobile ? 85 : 120) : (isMobile ? 110 : 140));
                          const isFixed = isDateCol || isTimeCol;
                          const activeFilterValues = col.id === "notes"
                            ? (baseColumnFilterValues.notes || "").trim() ? [baseColumnFilterValues.notes] : []
                            : (questionFilterValues[col.id] || []);
                          const columnIndex =
                            visibleBaseColumnsInOrder.length +
                            extraIndex;

                          const extraIdxOrder = questionColumnOrder.indexOf(col.id);
                          const prevVisibleExtraId = extraIndex > 0 ? visibleExtraColumns[extraIndex - 1].id : null;
                          const prevVisibleIdxOrder = prevVisibleExtraId ? questionColumnOrder.indexOf(prevVisibleExtraId) : -1;

                          // Initial gap within extra columns
                          let hiddenExtraIdsInGap = questionColumnOrder.slice(prevVisibleIdxOrder + 1, extraIdxOrder).filter(id => !selectedQuestionColumnIds.includes(id));

                          // If this is the FIRST extra column, also check for hidden base columns after the last visible base column
                          if (extraIndex === 0) {
                            const lastVisibleBaseId = visibleBaseColumnsInOrder[visibleBaseColumnsInOrder.length - 1];
                            const lastVisibleBaseIdxOrder = lastVisibleBaseId ? baseColumnOrder.indexOf(lastVisibleBaseId) : -1;
                            const hiddenTrailingBaseIds = baseColumnOrder.slice(lastVisibleBaseIdxOrder + 1).filter(id =>
                              !selectedBaseColumnIds.includes(id) &&
                              (id !== "facility" || showFacilityColumn) &&
                              (id !== "type" || filters.type === "all")
                            );
                            hiddenExtraIdsInGap = [...hiddenTrailingBaseIds, ...hiddenExtraIdsInGap];
                          }

                          const hiddenExtraLabels = hiddenExtraIdsInGap.map(id => {
                            if (BASE_COLUMN_LABELS[id]) return BASE_COLUMN_LABELS[id];
                            return extraColumns.find(c => c.id === id)?.label || id;
                          });

                          return (
                            <DraggableTh
                              id={col.id}
                              key={col.id}
                              data-column-index={columnIndex}
                              sx={{
                                bgcolor: activeColumnId === col.id
                                  ? "#DBEAFE"
                                  : ((filters.search || "").trim() && selectedSearchColumnIds.includes(col.id) && columnSearchMatches[col.id]?.length > 0)
                                    ? "#FEF3C7" // Yellow background for search matches
                                    : (sortBy === col.id ? "#eff6ff" : (highlightedColumnIndex === columnIndex ? "#DBEAFE" : "#F9FAFB")),
                                color: sortBy === col.id ? PRIMARY : "#374151",
                                fontWeight: 600,
                                fontSize: isMobile ? "0.6875rem" : "0.75rem",
                                letterSpacing: "0.5px",
                                textTransform: "uppercase",
                                borderBottom: "2px solid #e5e7eb",
                                borderRight: "1px solid #f3f4f6",
                                py: isMobile ? 1 : 2,
                                px: isMobile ? 1 : 2,
                                whiteSpace: "nowrap",
                                minWidth: width,
                                width: isFixed ? width : "auto",
                                maxWidth: 200,
                                overflow: "hidden",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  bgcolor: activeColumnId === col.id
                                    ? "#DBEAFE"
                                    : (filters.search || "").trim() && selectedSearchColumnIds.includes(col.id) && columnSearchMatches[col.id]?.length > 0
                                      ? "#FDE68A" : "#f1f5f9", // Darker yellow on hover
                                }
                              }}
                            >
                              {!isMobile && hiddenExtraLabels.length > 0 && (
                                <Tooltip
                                  title={
                                    <Box sx={{ p: 0.5 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5, color: "#111827" }}>Hidden Columns:</Typography>
                                      {hiddenExtraLabels.map(lbl => <Typography key={lbl} variant="caption" sx={{ display: "block", color: "#374151" }}>• {lbl}</Typography>)}
                                      <Typography variant="caption" sx={{ display: "block", mt: 0.5, fontStyle: "italic", opacity: 0.8, color: "#6b7280" }}>Double-click to unhide</Typography>
                                    </Box>
                                  }
                                  arrow
                                  placement="top"
                                  slotProps={{
                                    tooltip: {
                                      sx: {
                                        bgcolor: "#fff",
                                        color: "#111827",
                                        border: "1px solid #e5e7eb",
                                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                        "& .MuiTooltip-arrow": {
                                          color: "#fff",
                                          "&::before": {
                                            border: "1px solid #e5e7eb",
                                          }
                                        }
                                      }
                                    }
                                  }}
                                >
                                  <Box
                                    onDoubleClick={(e) => {
                                      e.stopPropagation();
                                      // Determine which list these IDs belong to
                                      const baseIds = hiddenExtraIdsInGap.filter(id => BASE_COLUMN_IDS.includes(id));
                                      const extraIds = hiddenExtraIdsInGap.filter(id => !BASE_COLUMN_IDS.includes(id));

                                      if (baseIds.length > 0) {
                                        setSelectedBaseColumnIds(prev => [...new Set([...prev, ...baseIds])]);
                                      }
                                      if (extraIds.length > 0) {
                                        setSelectedQuestionColumnIds(prev => [...new Set([...prev, ...extraIds])]);
                                      }
                                    }}
                                    sx={{
                                      position: "absolute",
                                      left: -2,
                                      top: 0,
                                      bottom: 0,
                                      width: 6,
                                      bgcolor: "#111827",
                                      cursor: "pointer",
                                      zIndex: 2,
                                      "&:hover": { bgcolor: PRIMARY, width: 8, left: -3 }
                                    }}
                                  />
                                </Tooltip>
                              )}
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: "100%", maxWidth: 184, minHeight: 44, justifyContent: "space-between" }}>
                                <Box
                                  onMouseEnter={(e) => handleHeaderMouseEnter(e, col.id, col.label)}
                                  onMouseLeave={() => setActiveHeaderTooltip(null)}
                                  onTouchStart={(e) => handleHeaderMouseEnter(e, col.id, col.label)}
                                  sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, width: "100%" }}
                                >
                                  <Tooltip
                                    title={activeHeaderTooltip?.id === col.id ? activeHeaderTooltip.title : ""}
                                    arrow
                                    placement="top"
                                    slotProps={{
                                      tooltip: {
                                        sx: {
                                          bgcolor: "#fff",
                                          color: "#111827",
                                          border: "1px solid #e5e7eb",
                                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                          "& .MuiTooltip-arrow": {
                                            color: "#fff",
                                            "&::before": {
                                              border: "1px solid #e5e7eb",
                                            }
                                          }
                                        }
                                      }
                                    }}
                                  >
                                    <TableSortLabel
                                      active={sortBy === col.id}
                                      direction={sortBy === col.id ? sortDirection : "asc"}
                                      onClick={() => handleSort(col.id)}
                                      sx={{
                                        color: sortBy === col.id ? `${PRIMARY} !important` : "inherit !important",
                                        "& .MuiTableSortLabel-icon": {
                                          color: "#1e3a8a !important",
                                          opacity: sortBy === col.id ? 1 : 0.3,
                                          fontSize: sortBy === col.id ? "22px !important" : "inherit !important",
                                        },
                                        maxWidth: "100%",
                                        "& .MuiTableSortLabel-root": {
                                          maxWidth: "100%",
                                        }
                                      }}
                                    >
                                      <Box
                                        component="span"
                                        className="truncated-text"
                                        sx={{
                                          display: isMobile ? "-webkit-box" : "block",
                                          WebkitLineClamp: isMobile ? 2 : "unset",
                                          WebkitBoxOrient: isMobile ? "vertical" : "unset",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: isMobile ? "normal" : "nowrap",
                                          lineHeight: isMobile ? 1.15 : "inherit",
                                          maxWidth: 140,
                                          fontWeight: sortBy === col.id ? 700 : 600
                                        }}
                                      >
                                        {col.label.toUpperCase()}
                                      </Box>
                                    </TableSortLabel>
                                  </Tooltip>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, alignSelf: "flex-end" }}>
                                  {/* Match count - clickable */}
                                  {(filters.search || "").trim() && selectedSearchColumnIds.includes(col.id) && columnSearchMatches[col.id]?.length > 0 && (
                                    <Tooltip title="Find next match" arrow>
                                      <Box
                                        onClick={(e) => { e.stopPropagation(); handleScrollToMatch(col.id, "next"); }}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          bgcolor: "rgba(255,255,255,0.9)",
                                          borderRadius: "12px",
                                          px: isMobile ? 0.8 : 1.2,
                                          py: isMobile ? 0.15 : 0.3,
                                          border: "1px solid #bae6fd",
                                          flexShrink: 0,
                                          cursor: "pointer",
                                          transition: "all 0.2s",
                                          "&:hover": { bgcolor: "#e0f2fe" }
                                        }}
                                      >
                                        <Typography sx={{ fontSize: isMobile ? "0.55rem" : "0.65rem", fontWeight: 800, color: "#0369a1", whiteSpace: "nowrap" }}>
                                          Find: {(currentSearchMatchIndices[col.id] ?? -1) + 1} / {columnSearchMatches[col.id].length}
                                        </Typography>
                                      </Box>
                                    </Tooltip>
                                  )}
                                  <Tooltip title="Filter column" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setColumnFilterAnchor({
                                          id: col.id,
                                          anchorEl: e.currentTarget,
                                        });
                                      }}
                                      sx={{
                                        p: isMobile ? 0.25 : 0.5,
                                        color: activeFilterValues.length ? PRIMARY : "#9ca3af",
                                        "&:hover": {
                                          color: PRIMARY,
                                          bgcolor: "#e5e7eb"
                                        },
                                      }}
                                    >
                                      <FilterAltIcon sx={{ fontSize: isMobile ? 14 : 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Hide column" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedQuestionColumnIds((prev) =>
                                          prev.filter((id) => id !== col.id)
                                        );
                                      }}
                                      sx={{
                                        p: isMobile ? 0.25 : 0.5,
                                        color: "#9ca3af",
                                        "&:hover": {
                                          color: "#ef4444",
                                          bgcolor: "#fee2e2"
                                        },
                                      }}
                                    >
                                      <VisibilityOffIcon sx={{ fontSize: isMobile ? 14 : 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            </DraggableTh>
                          );
                        })}
                      </SortableContext>
                      <DragOverlay
                        dropAnimation={{
                          duration: 400,
                          easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
                        }}
                      >
                        {activeColumnId ? (
                          <TableCell
                            sx={{
                              bgcolor: "#2259c9",
                              color: "#fff",
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              letterSpacing: "0.8px",
                              textTransform: "uppercase",
                              border: "2px solid #1e3a8a",
                              borderRadius: "4px",
                              py: 2,
                              px: 2,
                              whiteSpace: "nowrap",
                              minWidth: 140,
                              maxWidth: 200,
                              overflow: "hidden",
                              opacity: 0.95,
                              boxShadow: "0 8px 16px rgba(34, 89, 201, 0.25)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}
                          >
                            {BASE_COLUMN_LABELS[activeColumnId] || extraColumns.find(c => c.id === activeColumnId)?.label || activeColumnId}
                          </TableCell>
                        ) : null}
                      </DragOverlay>
                    </DndContext>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedPatients.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={Math.max(1, baseColumnCount)}
                        align={baseColumnCount > 8 ? "left" : "center"}
                        sx={{ p: 0, borderBottom: "none" }}
                      >
                        <div className={baseColumnCount > 8 ? styles.emptyStateLeft : styles.emptyState}>
                          <div className={styles.emptyIcon}>
                            <FormatListBulletedIcon sx={{ fontSize: 40 }} />
                          </div>
                          <div className={styles.emptyTitle}>No data found</div>
                          <div className={styles.emptySubtitle}>
                            {filters.search?.trim() || appliedAdvancedFilters.length > 0 || Object.keys(baseColumnFilterValues).some(k => baseColumnFilterValues[k])
                              ? "No data found for the applied search or filters."
                              : "No data found for the selected date range and defaults."}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedPatients.map((row, rowIndex) => (
                      <TableRow
                        key={row._id}
                        hover
                        sx={{
                          backgroundColor: rowIndex % 2 === 0 ? "#FFFFFF" : "#F9FAFB",
                          "&:hover": {
                            backgroundColor: "#EEF2FF",
                          },
                          "& td, & .MuiTableCell-root": { borderBottom: "1.5px solid #E5E7EB", py: isMobile ? 0.15 : 2, px: isMobile ? 0.15 : 2 },
                        }}
                      >
                        {filters.type !== 'all' && (
                          <TableCell
                            id={`actions_cell_${rowIndex}`}
                            data-column-index={-1}
                            sx={{
                              width: isMobile ? 40 : 80,
                              minWidth: isMobile ? 40 : 80,
                              px: isMobile ? 0.15 : 1,
                              py: isMobile ? 0.15 : 1,
                              borderRight: "3px solid #E5E7EB",
                              bgcolor: highlightedColumnIndex === -1 ? "rgba(37, 99, 235, 0.08)" : "#fff",
                              willChange: "transform",
                              transition: "background-color 0.3s ease",
                              position: "sticky",
                              left: 0,
                              zIndex: 20,
                              boxShadow: "2px 0 5px rgba(0,0,0,0.05)",
                            }}
                          >
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: isMobile ? 0.25 : 1 }}>
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                                {isWriteAccess && !isMobile && (
                                  <Tooltip title="Edit / Highlight" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditPatient(row);
                                        setPatientId(row._id);
                                        setShowAdd(true);
                                      }}
                                      sx={{ p: 0.5, color: row?.highlighter?.length > 0 ? "#ef4444" : "#4b5563" }}
                                    >
                                      <EditSVG fill={row?.highlighter?.length > 0 ? "#ef4444" : "#4b5563"} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <HtmlTooltip
                                  content={<ActivityLogTooltip logs={row?.logs ?? []} />}
                                  placement="right"
                                  sx={{
                                    "& .MuiTooltip-tooltip": {
                                      width: "360px",
                                      maxWidth: "360px",
                                      padding: 0,
                                    },
                                  }}
                                >
                                  <IconButton size="small" sx={{ p: isMobile ? 0.2 : 0.5, color: "#4b5563" }}>
                                    <InfoIcon sx={{ fontSize: isMobile ? 16 : 20 }} />
                                  </IconButton>
                                </HtmlTooltip>
                              </Box>

                              {/* Highlighter Dots */}
                              <Box sx={{ display: "flex", flexDirection: isMobile ? "row" : "column", alignItems: "center", justifyContent: "center", flexWrap: "wrap", gap: 0.5 }}>
                                {row?.highlighter &&
                                  row?.highlighter.length > 0 &&
                                  row?.highlighter
                                    .filter((ele) => !_.includes(["suffix", "middleInitial"], ele?.fieldName))
                                    .map((ele, hIndex) => (
                                      <Tooltip
                                        key={hIndex}
                                        title={ele?.description || ""}
                                        arrow
                                        placement="right"
                                      >
                                        <Box
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleScrollTo(`${ele?.fieldName}_cell_${rowIndex}`, ele?.color);
                                          }}
                                          sx={{
                                            width: isMobile ? 7 : 10,
                                            height: isMobile ? 7 : 10,
                                            borderRadius: "50%",
                                            bgcolor: ele?.color,
                                            cursor: "pointer",
                                            "&:hover": { transform: "scale(1.2)" },
                                            transition: "transform 0.2s"
                                          }}
                                        />
                                      </Tooltip>
                                    ))}
                              </Box>
                            </Box>
                          </TableCell>
                        )}
                        {visibleBaseColumnsInOrder.map((baseId, baseIndex) => {
                          const colIdx = baseIndex;
                          const isDraggingThis = activeColumnId === baseId;
                          const isHighlighted = highlightedColumnIndex === colIdx;
                          const isPinned = filters.type !== "all" && ["facility", "lastName", "firstName"].includes(baseId);
                          let pinnedLeft = undefined;
                          if (isPinned) {
                            pinnedLeft = isMobile ? 40 : 80;
                            if (baseId === "lastName" && showFacilityColumn) pinnedLeft += (isMobile ? 100 : 140);
                            if (baseId === "firstName") {
                              pinnedLeft += (isMobile ? 100 : 140);
                              if (showFacilityColumn) pinnedLeft += (isMobile ? 100 : 140);
                            }
                          }

                          const cellHighlight = (isDraggingThis || isHighlighted)
                            ? { bgcolor: "#DBEAFE", transition: "background-color 0.2s ease" } : {};

                          const cellSx = {
                            color: "#4b5563",
                            fontWeight: 500,
                            fontSize: isMobile ? "0.75rem" : "0.8125rem",
                            ...cellHighlight,
                            position: isPinned ? "sticky" : "relative",
                            left: pinnedLeft,
                            zIndex: isPinned ? 20 : 1,
                            borderRight: isPinned ? "3px solid #E5E7EB" : "1px solid #E5E7EB",
                            boxShadow: isPinned ? "2px 0 5px rgba(0,0,0,0.05)" : "none",
                            bgcolor: isPinned ? (rowIndex % 2 === 0 ? "#FFFFFF" : "#F9FAFB") : "inherit",
                            willChange: isPinned ? "transform" : "auto",
                            py: isMobile ? 0.15 : 2,
                            px: isMobile ? 0.15 : 2,
                          };
                          const cellId = `${baseId}_cell_${rowIndex}`;

                          if (baseId === "facility") {
                            return <TableCell key={baseId} id={cellId} sx={{ ...cellSx, width: isMobile ? 100 : 140, minWidth: isMobile ? 100 : 140 }}>{highlightSearchMatch(_.get(row, "facilityId.name", "—"), "facility", rowIndex)}</TableCell>;
                          }
                          if (baseId === "firstName") {
                            return (
                              <TableCell key={baseId} id={cellId} sx={{ ...cellSx, color: "#111827", fontWeight: 600, width: isMobile ? 100 : 140, minWidth: isMobile ? 100 : 140 }}>
                                {highlightSearchMatch(row.firstName || "—", "firstName", rowIndex)}
                              </TableCell>
                            );
                          }
                          if (baseId === "lastName") {
                            return (
                              <TableCell key={baseId} id={cellId} sx={{ ...cellSx, color: "#111827", fontWeight: 600, width: isMobile ? 100 : 140, minWidth: isMobile ? 100 : 140 }}>
                                {highlightSearchMatch(row.lastName || "—", "lastName", rowIndex)}
                              </TableCell>
                            );
                          }
                          if (baseId === "middleInitial") {
                            return (
                              <TableCell key={baseId} id={cellId} sx={{
                                ...cellSx,
                                py: isMobile ? 0.15 : 2,
                                px: isMobile ? 0.15 : 1,
                                width: 50,
                                minWidth: 50,
                                textAlign: "center",
                              }}>
                                {highlightSearchMatch(row.middleInitial || "—", "middleInitial", rowIndex)}
                              </TableCell>
                            );
                          }
                          if (baseId === "dob") {
                            return (
                              <TableCell key={baseId} id={cellId} sx={cellSx}>
                                {highlightSearchMatch(row.DOB ? toDisplayTime(row.DOB, "MM/DD/YYYY") : "—", "dob", rowIndex)}
                              </TableCell>
                            );
                          }
                          if (baseId === "dateOfADT") {
                            return (
                              <TableCell key={baseId} id={cellId} sx={{ ...cellSx, ...cellHighlight }}>
                                <Typography sx={{ color: "#111827", fontWeight: 700, fontSize: isMobile ? "0.75rem" : "0.875rem" }}>
                                  {highlightSearchMatch(row.dateOfADT ? toDisplayTime(row.dateOfADT, "MM/DD/YYYY") : "—", "dateOfADT", rowIndex)}
                                </Typography>
                              </TableCell>
                            );
                          }
                          if (baseId === "type") {
                            return (
                              <TableCell key={baseId} id={cellId} sx={{ ...cellSx, fontWeight: 500 }}>
                                {(() => {
                                  const label = formatTypeLabel(row);
                                  const text = ["SNF", "AMA"].includes(label) ? label : _.capitalize(label);
                                  return highlightSearchMatch(text, "type", rowIndex);
                                })()}
                              </TableCell>
                            );
                          }
                          return null;
                        })}

                        {visibleExtraColumns.map((col, extraIndex) => {
                          const isDateCol = col.isDate || col.label.toLowerCase().includes("date");
                          const isTimeCol = col.isTime || col.label.toLowerCase().includes("time");

                          const width = isDateCol ? (isMobile ? 95 : 130) : (isTimeCol ? (isMobile ? 85 : 120) : (isMobile ? 110 : 140));
                          const isFixed = isDateCol || isTimeCol;
                          const colIdx = visibleBaseColumnsInOrder.length + extraIndex;
                          const isDraggingThis = activeColumnId === col.id;
                          const isHighlighted = highlightedColumnIndex === colIdx;
                          const cellHighlight = (isDraggingThis || isHighlighted)
                            ? { bgcolor: "#DBEAFE", transition: "background-color 0.2s ease" } : {};

                          const val = col.render ? col.render(row) : "—";
                          const valStr = String(val || "");

                          return (
                            <TableCell
                              key={col.id}
                              id={`${col.id}_cell_${rowIndex}`}
                              sx={{
                                color: "#4b5563",
                                fontWeight: 500,
                                minWidth: width,
                                width: isFixed ? width : "auto",
                                maxWidth: 200,
                                overflow: "hidden",
                                textOverflow: valStr.length > TRUNCATION_LIMIT ? "clip" : "ellipsis",
                                whiteSpace: "nowrap",
                                borderRight: (extraIndex < visibleExtraColumns.length - 1) ? "1.5px solid #E5E7EB" : "none",
                                fontSize: isMobile ? "0.75rem" : "0.8125rem",
                                py: isMobile ? 0.15 : 2,
                                px: isMobile ? 0.15 : 2,
                                ...cellHighlight,
                              }}
                            >
                              {(() => {
                                if (valStr.length > TRUNCATION_LIMIT) {
                                  return (
                                    <Tooltip title="Click to view full text" arrow>
                                      <Box
                                        component="span"
                                        onClick={(e) => {
                                          setCellValueAnchorEl(e.currentTarget);
                                          setCurrentCellValue(valStr);
                                          setCurrentCellColId(col.id);
                                          setCurrentCellRowIndex(rowIndex);
                                        }}
                                        sx={{
                                          cursor: "pointer",
                                          color: "inherit",
                                          borderBottom: "1px dashed #9ca3af",
                                          "&:hover": { color: PRIMARY, borderBottom: `1px dashed ${PRIMARY}` },
                                        }}
                                      >
                                        {highlightSearchMatch(valStr, col.id, rowIndex, TRUNCATION_LIMIT)}
                                      </Box>
                                    </Tooltip>
                                  );
                                }
                                return highlightSearchMatch(valStr, col.id, rowIndex);
                              })()}
                            </TableCell>
                          );
                        })}


                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {baseColumnFilterAnchor.id && (
              <Menu
                anchorEl={baseColumnFilterAnchor.anchorEl}
                open={Boolean(baseColumnFilterAnchor.anchorEl)}
                onClose={() => setBaseColumnFilterAnchor({ id: null, anchorEl: null })}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{ sx: { minWidth: 220, maxWidth: "90vw", p: 1.5 } }}
              >
                {isMobile && (
                  <>
                    <MenuItem
                      onClick={() => {
                        const id = baseColumnFilterAnchor.id;
                        setSelectedBaseColumnIds((prev) => prev.filter((colId) => colId !== id));
                        setBaseColumnFilterAnchor({ id: null, anchorEl: null });
                      }}
                    >
                      <ListItemText primary="Hide Column" />
                    </MenuItem>
                    <Divider />
                  </>
                )}
                {(() => {
                  const id = baseColumnFilterAnchor.id;
                  const hasFilter =
                    (["facility", "firstName", "middleInitial", "lastName", "type"].includes(id) && baseColumnFilterValues[id]) ||
                    (id === "dob" && (baseColumnFilterValues.dobFrom || baseColumnFilterValues.dobTo)) ||
                    (id === "dateOfADT" && (baseColumnFilterValues.dateOfADTFrom || baseColumnFilterValues.dateOfADTTo));

                  if (!hasFilter) return null;

                  return (
                    <>
                      <MenuItem
                        onClick={() => {
                          setBaseColumnFilterValues((prev) => {
                            const next = { ...prev };
                            if (id === "facility") next.facility = "";
                            if (id === "firstName") next.firstName = "";
                            if (id === "middleInitial") next.middleInitial = "";
                            if (id === "lastName") next.lastName = "";
                            if (id === "type") next.type = "";
                            if (id === "dob") { next.dobFrom = ""; next.dobTo = ""; }
                            if (id === "dateOfADT") { next.dateOfADTFrom = ""; next.dateOfADTTo = ""; }
                            return next;
                          });
                        }}
                      >
                        <ListItemText primary="Clear filter" />
                      </MenuItem>
                      <Divider />
                    </>
                  );
                })()}
                {["facility", "firstName", "middleInitial", "lastName", "type"].includes(baseColumnFilterAnchor.id) && (
                  <Box sx={{ px: 1.5, py: 0.5 }}>
                    <TextField
                      size="small"
                      fullWidth
                      autoFocus
                      placeholder={
                        baseColumnFilterAnchor.id === "facility" ? "Filter by facility..." :
                          baseColumnFilterAnchor.id === "firstName" ? "Filter by first name..." :
                            baseColumnFilterAnchor.id === "middleInitial" ? "Filter by middle initial..." :
                              baseColumnFilterAnchor.id === "type" ? "Filter by type..." :
                                "Filter by last name..."
                      }
                      value={
                        baseColumnFilterAnchor.id === "facility"
                          ? baseColumnFilterValues.facility
                          : baseColumnFilterAnchor.id === "firstName"
                            ? baseColumnFilterValues.firstName
                            : baseColumnFilterAnchor.id === "middleInitial"
                              ? baseColumnFilterValues.middleInitial
                              : baseColumnFilterAnchor.id === "type"
                                ? baseColumnFilterValues.type
                                : baseColumnFilterValues.lastName
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        setBaseColumnFilterValues((prev) => ({
                          ...prev,
                          ...(
                            baseColumnFilterAnchor.id === "facility" ? { facility: v } :
                              baseColumnFilterAnchor.id === "firstName" ? { firstName: v } :
                                baseColumnFilterAnchor.id === "middleInitial" ? { middleInitial: v } :
                                  baseColumnFilterAnchor.id === "type" ? { type: v } :
                                    { lastName: v }
                          ),
                        }));
                      }}
                      sx={{ "& .MuiInputBase-input": { fontSize: "0.875rem" } }}
                    />
                  </Box>
                )}
                {baseColumnFilterAnchor.id === "dob" && (
                  <Box sx={{ px: 1.5, py: 0.5, display: "flex", flexDirection: "column", gap: 1 }}>
                    <TextField
                      size="small"
                      type="date"
                      label="From"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={baseColumnFilterValues.dobFrom}
                      onChange={(e) => setBaseColumnFilterValues((prev) => ({ ...prev, dobFrom: e.target.value }))}
                      sx={{ "& .MuiInputBase-input": { fontSize: "0.875rem" } }}
                    />
                    <TextField
                      size="small"
                      type="date"
                      label="To"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={baseColumnFilterValues.dobTo}
                      onChange={(e) => setBaseColumnFilterValues((prev) => ({ ...prev, dobTo: e.target.value }))}
                      sx={{ "& .MuiInputBase-input": { fontSize: "0.875rem" } }}
                    />
                  </Box>
                )}
                {baseColumnFilterAnchor.id === "dateOfADT" && (
                  <Box sx={{ px: 1.5, py: 0.5, display: "flex", flexDirection: "column", gap: 1 }}>
                    <TextField
                      size="small"
                      type="date"
                      label="From"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={baseColumnFilterValues.dateOfADTFrom}
                      onChange={(e) => setBaseColumnFilterValues((prev) => ({ ...prev, dateOfADTFrom: e.target.value }))}
                      sx={{ "& .MuiInputBase-input": { fontSize: "0.875rem" } }}
                    />
                    <TextField
                      size="small"
                      type="date"
                      label="To"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={baseColumnFilterValues.dateOfADTTo}
                      onChange={(e) => setBaseColumnFilterValues((prev) => ({ ...prev, dateOfADTTo: e.target.value }))}
                      sx={{ "& .MuiInputBase-input": { fontSize: "0.875rem" } }}
                    />
                  </Box>
                )}
              </Menu>
            )}
            {columnFilterAnchor.id && (
              <Menu
                anchorEl={columnFilterAnchor.anchorEl}
                open={Boolean(columnFilterAnchor.anchorEl)}
                onClose={() => setColumnFilterAnchor({ id: null, anchorEl: null })}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                MenuListProps={{ dense: true }}
                PaperProps={{
                  sx: {
                    maxWidth: "90vw",
                    "& .MuiMenuItem-root": {
                      minHeight: 28,
                      py: 0.25,
                      px: 1.5,
                    },
                    "& .MuiListItemText-primary": {
                      fontSize: "0.75rem",
                    },
                  },
                }}
              >
                {isMobile && (
                  <>
                    <MenuItem
                      onClick={() => {
                        const id = columnFilterAnchor.id;
                        setSelectedQuestionColumnIds((prev) =>
                          prev.filter((colId) => colId !== id)
                        );
                        setColumnFilterAnchor({ id: null, anchorEl: null });
                      }}
                    >
                      <ListItemText primary="Hide Column" />
                    </MenuItem>
                    <Divider />
                  </>
                )}
                {(() => {
                  const id = columnFilterAnchor.id;
                  const hasFilter =
                    id === "notes"
                      ? !!(baseColumnFilterValues.notes || "").trim()
                      : (questionFilterValues[id] || []).length > 0;

                  if (!hasFilter) return null;

                  return (
                    <>
                      <MenuItem
                        onClick={() => {
                          if (id === "notes") {
                            setBaseColumnFilterValues((prev) => ({
                              ...prev,
                              notes: "",
                            }));
                          } else {
                            setQuestionFilterValues((prev) => ({
                              ...prev,
                              [id]: [],
                            }));
                          }
                        }}
                      >
                        <ListItemText primary="Clear filter" />
                      </MenuItem>
                      <Divider />
                    </>
                  );
                })()}
                {columnFilterAnchor.id === "notes" ? (
                  <Box sx={{ px: 1.5, py: 0.5 }}>
                    <TextField
                      size="small"
                      fullWidth
                      autoFocus
                      placeholder="Filter by notes..."
                      value={baseColumnFilterValues.notes}
                      onChange={(e) => {
                        const v = e.target.value;
                        setBaseColumnFilterValues((prev) => ({
                          ...prev,
                          notes: v,
                        }));
                      }}
                      sx={{ "& .MuiInputBase-input": { fontSize: "0.875rem" } }}
                    />
                  </Box>
                ) : (
                  (extraColumnFilterOptions[columnFilterAnchor.id] || []).map(
                    (opt) => {
                      const selectedVals =
                        questionFilterValues[columnFilterAnchor.id] || [];
                      const checked = selectedVals.includes(opt);
                      return (
                        <MenuItem
                          key={opt}
                          onClick={() => {
                            setQuestionFilterValues((prev) => {
                              const prevVals = prev[columnFilterAnchor.id] || [];
                              const exists = prevVals.includes(opt);
                              const nextVals = exists
                                ? prevVals.filter((v) => v !== opt)
                                : [...prevVals, opt];
                              return {
                                ...prev,
                                [columnFilterAnchor.id]: nextVals,
                              };
                            });
                          }}
                        >
                          <Checkbox size="small" checked={checked} />
                          <ListItemText primary={opt} />
                        </MenuItem>
                      );
                    }
                  )
                )}
              </Menu>
            )}
            {hiddenColumnsAnchorEl && (
              <Menu
                anchorEl={hiddenColumnsAnchorEl}
                open={Boolean(hiddenColumnsAnchorEl)}
                onClose={() => setHiddenColumnsAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                MenuListProps={{ dense: true }}
                PaperProps={{ sx: { maxWidth: "90vw" } }}
              >
                {hiddenBaseColumns.length === 0 && hiddenExtraColumns.length === 0 ? (
                  <MenuItem disabled>
                    <ListItemText primary="No hidden columns" />
                  </MenuItem>
                ) : [
                  <MenuItem
                    key="unhide-all"
                    onClick={() => {
                      setSelectedBaseColumnIds((prev) => {
                        const toAdd = hiddenBaseColumns.map((c) => c.id).filter((id) => !prev.includes(id));
                        return toAdd.length ? [...prev, ...toAdd] : prev;
                      });
                      setSelectedQuestionColumnIds((prev) => {
                        const toAdd = hiddenExtraColumns.map((c) => c.id).filter((id) => !prev.includes(id));
                        return toAdd.length ? [...prev, ...toAdd] : prev;
                      });
                      setHiddenColumnsAnchorEl(null);
                    }}
                    sx={{ fontWeight: 600, borderBottom: "1px solid #E8E8EB", mb: 0.5, pb: 0.5 }}
                  >
                    <ListItemText primary="Unhide all columns" />
                  </MenuItem>,
                  ...hiddenBaseColumns.map((col) => (
                    <MenuItem
                      key={`base-${col.id}`}
                      onClick={() => {
                        setSelectedBaseColumnIds((prev) => (prev.includes(col.id) ? prev : [...prev, col.id]));
                        if (hiddenBaseColumns.length === 1 && hiddenExtraColumns.length === 0) setHiddenColumnsAnchorEl(null);
                      }}
                    >
                      <ListItemText primary={col.label} />
                    </MenuItem>
                  )),
                  ...hiddenExtraColumns.map((col) => (
                    <MenuItem
                      key={col.id}
                      onClick={() => {
                        setSelectedQuestionColumnIds((prev) => (prev.includes(col.id) ? prev : [...prev, col.id]));
                        if (hiddenExtraColumns.length === 1 && hiddenBaseColumns.length === 0) setHiddenColumnsAnchorEl(null);
                      }}
                    >
                      <ListItemText primary={col.label} />
                    </MenuItem>
                  ))
                ]}
              </Menu>
            )}
            <Popover
              open={Boolean(searchColumnAnchorEl)}
              anchorEl={searchColumnAnchorEl}
              onClose={() => {
                setSearchColumnAnchorEl(null);
                setSearchColumnQuery("");
              }}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              slotProps={{ paper: { sx: { mt: 0.5, minWidth: 260, maxWidth: "90vw", maxHeight: 380 } } }}
            >
              <Box sx={{ py: 0.5 }}>
                <Box sx={{ px: 1.5, pt: 1, pb: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Search columns..."
                    value={searchColumnQuery}
                    onChange={(e) => setSearchColumnQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: 1, bgcolor: "#f9fafb", "& fieldset": { borderColor: "#E5E7EB" } },
                    }}
                    sx={{ "& .MuiInputBase-root": { fontSize: "0.875rem" } }}
                    fullWidth
                  />
                </Box>
                <Box sx={{ maxHeight: 280, overflowY: "auto" }}>
                  {filteredColumnsForSearch.length === 0 ? (
                    <MenuItem disabled sx={{ fontSize: "0.875rem" }}>
                      <ListItemText primary="No columns match" />
                    </MenuItem>
                  ) : (
                    filteredColumnsForSearch.map((item) => (
                      <MenuItem
                        key={item.id}
                        dense
                        onClick={() => handleScrollToColumn(item.columnIndex)}
                        sx={{ fontSize: "0.875rem" }}
                      >
                        <ListItemText primary={item.label} />
                      </MenuItem>
                    ))
                  )}
                </Box>
              </Box>
            </Popover>
          </Box>
        )}
      </Box>
      <Drawer
        anchor="right"
        open={Boolean(selectedResident)}
        onClose={() => setSelectedResident(null)}
        PaperProps={{
          sx: {
            width: 420,
            maxWidth: "100%",
            boxShadow: "0 8px 32px rgba(15, 23, 42, 0.18)",
            borderRadius: "16px 0 0 16px",
          },
        }}
      >
        {selectedResident && (
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Box
              sx={{
                px: 2.5,
                py: 1.5,
                borderBottom: "1px solid #e8e8eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#111827" }}>
                  Resident details
                </Typography>
                <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.25 }}>
                  {selectedResident.lastName} {selectedResident.firstName}{" "}
                  {selectedResident.middleInitial || ""}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setSelectedResident(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box sx={{ px: 2.5, py: 2, overflowY: "auto", flex: 1 }}>
              <Typography variant="overline" sx={{ color: "#9ca3af", letterSpacing: "0.08em" }}>
                Summary
              </Typography>
              <Box
                sx={{
                  mt: 1.25,
                  mb: 2,
                  p: 1.75,
                  borderRadius: 2,
                  bgcolor: "#f9fafb",
                  border: "1px solid #e5e7eb",
                }}
              >
                <Stack spacing={1}>
                  <Typography variant="body2">
                    <strong>Facility:</strong> {_.get(selectedResident, "facilityId.name", "—")}
                  </Typography>
                  <Typography variant="body2">
                    <strong>DOB:</strong>{" "}
                    {selectedResident.DOB ? toDisplayTime(selectedResident.DOB) : "—"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Type:</strong> {formatTypeLabel(selectedResident)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Date of ADT:</strong>{" "}
                    {selectedResident.dateOfADT ? toDisplayTime(selectedResident.dateOfADT) : "—"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Insurance:</strong>{" "}
                    {_.get(selectedResident, "insurance.label", "—")}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Unit:</strong> {_.get(selectedResident, "unit.label", "—")}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Doctor:</strong> {_.get(selectedResident, "doctor.label", "—")}
                  </Typography>
                </Stack>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="overline" sx={{ color: "#9ca3af", letterSpacing: "0.08em" }}>
                History
              </Typography>

              {detailsLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 4,
                  }}
                >
                  <CircularProgress size={24} sx={{ color: PRIMARY }} />
                </Box>
              ) : detailsList.length === 0 ? (
                <Typography variant="body2" sx={{ color: "#6b7280", mt: 1.5 }}>
                  No additional history found for this resident.
                </Typography>
              ) : (
                <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                  {detailsList.map((item) => (
                    <Box
                      key={item._id}
                      sx={{
                        borderRadius: 1.5,
                        border: "1px solid #e5e7eb",
                        p: 1.25,
                        bgcolor: "#f9fafb",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                        {item.type
                          ? item.type.replace(/^./, (str) => str.toUpperCase())
                          : "—"}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#4b5563", fontSize: "0.8125rem" }}>
                        {item.dateOfADT ? toDisplayTime(item.dateOfADT) : "—"}
                      </Typography>
                      {item.notes && (
                        <Typography
                          variant="body2"
                          sx={{ color: "#6b7280", mt: 0.5, fontSize: "0.8125rem" }}
                        >
                          {item.notes}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="overline" sx={{ color: "#9ca3af", letterSpacing: "0.08em" }}>
                Activity log
              </Typography>

              {!selectedResident.logs || selectedResident.logs.length === 0 ? (
                <Typography variant="body2" sx={{ color: "#6b7280", mt: 1.5 }}>
                  No activity recorded for this resident yet.
                </Typography>
              ) : (
                <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                  {selectedResident.logs
                    .slice()
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .map((log) => (
                      <Box key={log._id || `${log.date}-${log.description}`} sx={{ display: "flex", gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: PRIMARY,
                            mt: 0.7,
                            flexShrink: 0,
                          }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {log.description || "Update"}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#6b7280", display: "block", mt: 0.25 }}>
                            {(log.userName || "System")}
                            {log.date ? ` · ${toDisplayTime(log.date)}` : ""}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                </Stack>
              )}
            </Box>
          </Box>
        )}
      </Drawer>

      {shareReportState.open && (
        <ShareNotesDialog
          handleClose={handleCloseShareReport}
          handleSubmit={handleShareReport}
          isShare={shareReportState.open}
          noteText={`Report: ${shareReportState.reportTitle || "Residents List"}`}
          users={users}
          availableIntegrations={availableIntegrations}
          isSubmitting={shareReportState.isSharing}
          accountId={accountIdFromHook}
          facilityId={facilityIdFromHook}
          redirectAfter={handleCloseShareReport}
        />
      )}

      <Popover
        open={Boolean(cellValueAnchorEl)}
        anchorEl={cellValueAnchorEl}
        onClose={() => setCellValueAnchorEl(null)}
        anchorReference={isMobile ? "none" : undefined}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        BackdropProps={isMobile ? {
          invisible: false,
          sx: {
            backgroundColor: "rgba(15, 23, 42, 0.3)",
            backdropFilter: "blur(2px)"
          }
        } : undefined}
        slotProps={{
          paper: {
            sx: isMobile ? {
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%) !important",
              width: "95vw",
              maxWidth: 500,
              maxHeight: "85vh",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)",
              borderRadius: "16px",
              display: "flex",
              flexDirection: "column",
              bgcolor: "#fff",
              overflow: "hidden",
            } : {
              p: 2,
              maxWidth: 400,
              maxHeight: 400,
              overflowY: "auto",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              borderRadius: 2,
            },
          },
        }}
      >
        {isMobile ? (
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%", maxHeight: "85vh" }}>
            {/* Header: Fixed */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2.5,
                py: 2,
                borderBottom: "1px solid #F1F5F9",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0F172A", fontSize: "0.95rem" }}>
                Details View
              </Typography>
              <IconButton
                size="small"
                onClick={() => setCellValueAnchorEl(null)}
                sx={{
                  color: "#64748B",
                  bgcolor: "#F8FAFC",
                  "&:hover": { bgcolor: "#F1F5F9", color: "#0F172A" },
                  p: 0.5,
                }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            {/* Scrollable Content Container */}
            <Box sx={{ p: 2.5, overflowY: "auto", flex: 1 }}>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", color: "#334155", fontSize: "0.85rem", lineHeight: 1.6 }}>
                {highlightSearchMatch(currentCellValue, currentCellColId, currentCellRowIndex)}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", color: "#374151" }}>
            {highlightSearchMatch(currentCellValue, currentCellColId, currentCellRowIndex)}
          </Typography>
        )}
      </Popover>

      {showAdd && (
        <AddPatient
          close={() => {
            setShowAdd(false);
            setEditPatient(null);
            setPatientId(null);
          }}
          selectedResident={editPatient}
          patientId={patientId}
          step={3}
          isMultipleFacility={permission?.isMultipleFacility}
          initialDate={initialDate}
          dialogZIndex={1300}
          patientUpdated={(updatedData) => {
            setPatients((prev) =>
              prev.map((p) => (p._id === updatedData._id ? updatedData : p))
            );
            if (selectedResident?._id === updatedData._id) {
              setSelectedResident(updatedData);
            }
          }}
        />
      )}

      {isMobile && (
        <RotateScreenDialog
          open={showRotateAlert}
          onClose={() => setIsDismissed(true)}
          title="Rotate Your Device"
          message="Please rotate your device to landscape mode to view the resident list properly"
        />
      )}
    </div>
  );
};

export default ResidenceList;
